import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import StatsCard from '../../components/ui/StatsCard';
import SubcontractCombobox from '../../components/ui/SubcontractCombobox';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { subInvoiceService } from '../../services/subInvoiceService';
import subcontractorService from '../../services/subcontractorService';
import subcontractService from '../../services/subcontractService';
import { projectService } from '../../services/projectService';
import { projectCostService } from '../../services/projectCostService';
import { projectExtrasService } from '../../services/projectExtrasService';
import { getSubcontractSummary } from '../../services/subcontractFinancialService';
import { supabase } from '../../lib/supabase';
import paymentRequestService from '../../services/paymentRequestService';
import useTranslation from '../../hooks/useTranslation';
import DocumentsModal from './components/DocumentsModal';
import { listInvoiceDocuments } from '../../services/invoiceDocumentsService';
import PortalDropdown from '../../components/ui/PortalDropdown';
import FilterDropdown from '../../components/ui/StatusFilterDropdown';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatDate, formatDateLong } from '../../utils/formatDate';
import { statusVariant } from '../../utils/statusVariant';
import Badge, { BADGE_VARIANTS } from '../../components/ui/Badge';
import BulkActionBar from '../../components/ui/BulkActionBar';

/* ─── Feature Flag ────────────────────────────────────────────────────────── */
const ENABLE_PRODUCTION_VALIDATION_WARNING = false;

/* ─── Instant Tooltip (no browser delay) ─────────────────────────────────── */
function InstantTooltip({ label, children }) {
  return (
    <span className="relative group/tip inline-flex">
      {children}
      <span
        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5
          whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[10px] text-white
          opacity-0 group-hover/tip:opacity-100 transition-opacity duration-0 z-[9999]"
      >
        {label}
      </span>
    </span>
  );
}

// ─── Helpers ─────────────────────────────────────────────────
const fmt = (val) => fmtCAD(val);

const fmtCAD = (val) => formatCurrency(val);

// La precaution 'T12:00:00' contre le decalage d'un jour vit desormais dans le
// helper central. La locale 'en-US' codee en dur, elle, a disparu.
const fmtDate = (d, language) => formatDate(d, language);

// Colonnes d'export, definies UNE fois : les trois exports en portaient chacun
// une copie litterale, en anglais. Meme motif que ExtrasPage et
// ClientInvoicesPage.
const EXPORT_COLUMNS = (t) => [
  t('subInvoices.export.columns.invoiceNumber'),
  t('subInvoices.export.columns.date'),
  t('subInvoices.export.columns.subcontractor'),
  t('subInvoices.export.columns.type'),
  t('subInvoices.export.columns.amountTaxIn'),
  t('subInvoices.export.columns.balance'),
  t('subInvoices.export.columns.status'),
];

// ─── Libelle d'une DP dans un selecteur ───────────────────────
// Toutes les DP sont selectionnables, approuvees comprises : un sous-traitant
// valide son pourcentage AVANT l'approbation et envoie sa facture APRES. Sans
// cela ces factures n'ont nulle part ou atterrir.
// Le statut est un suffixe TEXTE : une <option> native ne rend aucun balisage,
// le texte est donc la seule pastille possible. 'draft' n'en porte aucun.
const dpOptionLabel = (pr, t, prefix) => {
  const suffix =
    pr?.status === 'approved'  ? t(`${prefix}.dpApproved`)
    : pr?.status === 'submitted' ? t(`${prefix}.dpSubmitted`)
    : null;
  return suffix ? `DP${pr?.number} \u2014 ${suffix}` : `DP${pr?.number}`;
};

// ─── Status config (uses t() at render time via getStatusConfig) ──
const getStatusConfig = (t) => ({
  received:        { label: t('subInvoices.status.received'),        bg: 'bg-gray-100',    text: 'text-gray-600',   dot: 'bg-gray-400'    },
  pending_approval:{ label: t('subInvoices.status.pending_approval'), bg: 'bg-blue-100',    text: 'text-blue-600',   dot: 'bg-blue-500'    },
  approved_to_pay: { label: t('subInvoices.status.approved_to_pay'),  bg: 'bg-green-100',   text: 'text-green-600',  dot: 'bg-green-500'   },
  partially_paid:  { label: t('subInvoices.status.partially_paid'),   bg: 'bg-yellow-100',  text: 'text-yellow-700', dot: 'bg-yellow-500'  },
  paid:            { label: t('subInvoices.status.paid'),             bg: 'bg-emerald-100', text: 'text-emerald-700',dot: 'bg-emerald-500' },
  rejected:        { label: t('subInvoices.status.rejected'),         bg: 'bg-red-100',     text: 'text-red-700',    dot: 'bg-red-500'     }
});

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const STATUS_CONFIG = getStatusConfig(t);
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.received;
  return (
    <Badge variant={statusVariant('subInvoices', status)}>
      {cfg?.label}
    </Badge>
  );
};

const INVOICE_TYPE_CONFIG = {
  subcontract: { bg: 'bg-blue-50',   text: 'text-blue-700',   dot: 'bg-blue-400'   },
  extra:       { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-400' },
  credit:      { bg: 'bg-red-50',    text: 'text-red-600',    dot: 'bg-red-400'    },
};

const InvoiceTypeBadge = ({ invoiceType }) => {
  const { t } = useTranslation();
  const type = invoiceType || 'subcontract';
  const cfg = INVOICE_TYPE_CONFIG?.[type] || INVOICE_TYPE_CONFIG?.subcontract;
  const label = t(`subInvoices.invoiceType.${type}`);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${cfg?.bg} ${cfg?.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg?.dot}`} />
      {label}
    </span>
  );
};

// ─── Three-dot actions button ────────────────────────────────
// Le menu passe par PortalDropdown : rendu dans un portail, donc jamais
// coupe par un parent en overflow.

const ThreeDotButton = ({ invoice, actions }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  const items = actions?.map(action => ({
    label: action?.label,
    icon: action?.icon,
    onClick: action?.onClick,
    variant: action?.variant || 'default',
  }));

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        onClick={(e) => { e?.stopPropagation(); setOpen(prev => !prev); }}
        className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <Icon name="MoreVertical" size={14} />
      </button>
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        width={180}
      />
    </div>
  );
};

// ─── DocumentIconButton (Sub Invoices) ───────────────────────
const DocumentIconButton = ({ invoice, projectId, onClick, externalCount, onCountFetched }) => {
  const [docCount, setDocCount] = useState(0);
  const [docNames, setDocNames] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef(null);

  useEffect(() => {
    if (!invoice?.id || !projectId) return;

    // Source UNIQUE, partagee avec la modale Documents : les deux affichaient
    // deux reponses differentes pour le meme fichier.
    const fetchDocCount = async () => {
      try {
        const docs = await listInvoiceDocuments(invoice?.id, projectId);
        setDocCount(docs?.length || 0);
        setDocNames(docs?.map((d) => d?.name));
        if (onCountFetched) onCountFetched(invoice?.id, docs?.length || 0);
      } catch (err) {
        console.error('[DocumentIconButton] count failed:', err?.message);
        setDocCount(0);
        setDocNames([]);
      }
    };

    fetchDocCount();
  }, [invoice?.id, projectId]);

  const handleMouseEnter = () => {
    tooltipTimeout.current = setTimeout(() => setShowTooltip(true), 200);
  };

  const handleMouseLeave = () => {
    clearTimeout(tooltipTimeout?.current);
    setShowTooltip(false);
  };

  const hasDocuments = (externalCount ?? docCount) > 0;
  const displayCount = externalCount ?? docCount;

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        onClick={onClick}
        className="relative p-1 rounded transition-colors group"
        title="Documents"
      >
        <Icon
          name="FileText"
          size={13}
          className={`transition-colors ${hasDocuments ? 'text-orange-500' : 'text-gray-400'} group-hover:text-gray-700`}
        />
        <span
          className={`absolute -top-1 -right-1 text-[10px] rounded-full px-1 leading-tight font-medium ${
            hasDocuments ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-400'
          }`}
          style={{ minWidth: '14px', textAlign: 'center' }}
        >
          {displayCount}
        </span>
      </button>
      {showTooltip && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white shadow-md rounded-md px-2 py-1.5 min-w-[120px] max-w-[180px] pointer-events-none">
          {hasDocuments ? (
            <div className="space-y-0.5">
              {docNames?.slice(0, 3)?.map((name, i) => (
                <p key={i} className="text-[10px] text-gray-600 truncate">{name}</p>
              ))}
              {docCount > 3 && (
                <p className="text-[10px] text-gray-400">+{docCount - 3} more</p>
              )}
            </div>
          ) : (
            <p className="text-[10px] text-gray-500">No documents</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── InvoiceActionsMenu (3-dot) — portal-based, never clips inside table overflow
const InvoiceActionsMenu = ({ invoice, onEdit, onDelete, canDelete }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const { t } = useTranslation();

  const items = [
    {
      label: t('common.edit') || 'Edit',
      icon: 'Pencil',
      onClick: () => onEdit?.(invoice),
    },
    ...(canDelete
      ? [
          {
            label: t('subInvoices.actions.delete'),
            icon: 'Trash2',
            onClick: () => onDelete?.(invoice?.id),
            variant: 'danger',
          },
        ]
      : []),
  ];

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        title="Actions"
      >
        <Icon name="MoreVertical" size={15} />
      </button>
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        width={180}
      />
    </>
  );
};

// ─── Remise à reçue : confirmation avant de radier paiements / approbations ───
const ResetToReceivedDialog = ({ prompt, invoices, loading, onConfirm, onCancel }) => {
  const { t, language } = useTranslation();
  const impacts = prompt?.impacts || [];
  const hasPayment = impacts?.some(x => x?.payments?.length > 0);
  const net = impacts?.reduce((s, x) => s + (parseFloat(x?.approvals?.[0]?.net_payable) || 0), 0);
  const numberOf = (invoiceId) =>
    invoices?.find(inv => inv?.id === invoiceId)?.invoiceNumber || t('subInvoices.resetToReceived.noNumber');

  return (
    <ConfirmDialog
      isOpen
      title={t(hasPayment ? 'subInvoices.resetToReceived.paymentTitle' : 'subInvoices.resetToReceived.approvalTitle')}
      message={hasPayment
        ? t('subInvoices.resetToReceived.paymentMessage')
        : t('subInvoices.resetToReceived.approvalMessage', { net: fmtCAD(net) })}
      warning={hasPayment ? t('subInvoices.resetToReceived.irreversible') : null}
      icon={hasPayment ? 'AlertTriangle' : null}
      confirmVariant={hasPayment ? 'danger' : 'primary'}
      confirmLabel={t('subInvoices.resetToReceived.confirm')}
      cancelLabel={t('subInvoices.resetToReceived.cancel')}
      loading={loading}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {(hasPayment || impacts?.length > 1) && (
        <ul className="space-y-2 text-sm">
          {impacts?.map(x => (
            <li key={x?.invoiceId}>
              <p className="font-medium text-gray-900">
                {t('subInvoices.resetToReceived.invoiceLabel', { number: numberOf(x?.invoiceId) })}
              </p>
              {x?.payments?.length > 0 ? (
                x?.payments?.map(p => (
                  <p key={p?.id} className="text-gray-600 tabular-nums">
                    {t(p?.check_number ? 'subInvoices.resetToReceived.paymentLine' : 'subInvoices.resetToReceived.paymentLineNoCheck', {
                      check: p?.check_number,
                      amount: fmtCAD(p?.amount_paid),
                      date: p?.paid_date ? fmtDate(p?.paid_date, language) : '—',
                    })}
                  </p>
                ))
              ) : (
                <p className="text-gray-600 tabular-nums">
                  {t('subInvoices.resetToReceived.approvalLine', { net: fmtCAD(x?.approvals?.[0]?.net_payable) })}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </ConfirmDialog>
  );
};

// ─── Add Invoice Modal ────────────────────────────────────────
const AddInvoiceModal = ({ projectId, subcontractors, subcontracts, costCodes, extras, paymentRequests, onClose, onSave, initialData, mode }) => {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';

  // Derive initial invoice type from initialData
  const getInitialType = () => {
    if (!isEdit || !initialData) return 'subcontract';
    if (initialData?.creditId || initialData?.invoiceType === 'credit') return 'credit';
    if (initialData?.extraId || initialData?.invoiceType === 'extra') return 'extra';
    return 'subcontract';
  };

  const [invoiceType, setInvoiceType] = useState(getInitialType());

  const [form, setForm] = useState(() => {
    if (isEdit && initialData) {
      return {
        subcontractId: initialData?.subcontractId || initialData?.contractId || '',
        contractId: initialData?.contractId || '',
        extraId: initialData?.extraId || '',
        creditId: initialData?.creditId || '',
        invoiceNumber: initialData?.invoiceNumber || '',
        paymentRequestId: initialData?.paymentRequestId || '',
        invoiceDate: initialData?.invoiceDate || '',
        receivedDate: initialData?.receivedDate || '',
        notes: initialData?.notes || ''
      };
    }
    return { subcontractId: '', contractId: '', extraId: '', creditId: '', invoiceNumber: '', paymentRequestId: '', invoiceDate: '', receivedDate: '', notes: '' };
  });

  const [lines, setLines] = useState(() => {
    if (isEdit && initialData?.lines?.length > 0) {
      return initialData?.lines?.map(l => ({
        lineType: l?.lineType || 'cost_code',
        projectCostId: l?.projectCostId || '',
        extraCreditId: l?.extraCreditId || '',
        description: l?.description || '',
        amount: l?.amount !== undefined ? String(l?.amount) : '',
        subcontractId: l?.subcontractId || ''
      }));
    }
    return [{ lineType: 'cost_code', projectCostId: '', extraCreditId: '', description: '', amount: '', subcontractId: '' }];
  });

  /**
   * En edition, la case suit ce qui est STOCKE. Elle etait figee a true : sur
   * une facture sans taxes, rouvrir puis enregistrer recalculait le total AVEC
   * taxes et l'ecrasait — 14,975 % de plus, sur un chiffre qui alimente le
   * plafond d'approbation et le solde.
   *
   * `!== false` et non `=== true` : une colonne restee NULL sur une vieille
   * ligne doit valoir « avec taxes », le comportement historique. Seul un
   * false explicite decoche.
   *
   * En creation, on garde true — c'est le cas courant et le comportement
   * actuel.
   */
  const [applyTax, setApplyTax] = useState(
    isEdit && initialData ? initialData?.taxApplied !== false : true
  );
  const [amountDisplays, setAmountDisplays] = useState({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const addLine = () => setLines(p => [...p, { lineType: 'cost_code', projectCostId: '', extraCreditId: '', description: '', amount: '', subcontractId: '' }]);
  const removeLine = (i) => setLines(p => p?.filter((_, idx) => idx !== i));
  const setLine = (i, k, v) => setLines(p => p?.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const handleAmountFocus = (i) => {
    setAmountDisplays(prev => ({ ...prev, [i]: 'editing' }));
  };

  const handleAmountBlur = (i, val) => {
    setAmountDisplays(prev => ({ ...prev, [i]: 'formatted' }));
  };

  // Separate extras and credits from the combined list
  const extraOptions = extras?.filter(e => e?.type === 'extra') || [];
  const creditOptions = extras?.filter(e => e?.type === 'credit') || [];

  // Helper: get L&M subcontracts matching a given project_cost_id
  const getLaborMaterialSubcontracts = (projectCostId) => {
    if (!projectCostId) return [];
    return (subcontracts || [])?.filter(
      s => s?.projectCostId === projectCostId && s?.pricingModel === 'labor_material'
    );
  };

  // Remonte au-dessus de handleSave : ces valeurs alimentent desormais le pied
  // de la modale ET le payload. Une seule formule, deux usages.
  const lineTotal = lines?.reduce((s, l) => s + (parseFloat(l?.amount) || 0), 0);

  const subtotal = lineTotal;
  const tps = applyTax ? subtotal * 0.05 : 0;
  const tvq = applyTax ? subtotal * 0.09975 : 0;
  const grandTotal = subtotal + tps + tvq;

  const handleSave = async () => {
    setError('');
    // Validate: all invoice types require a subcontract
    if (!form?.subcontractId) {
      setError(t('subInvoices.addModal.errorSubcontract'));
      return;
    }
    if (invoiceType === 'extra' && !form?.extraId) {
      setError(t('subInvoices.addModal.errorExtraRequired'));
      return;
    }
    if (invoiceType === 'credit' && !form?.creditId) {
      setError(t('subInvoices.addModal.errorCreditRequired'));
      return;
    }
    if (lines?.length === 0) { setError(t('subInvoices.addModal.errorLines')); return; }
    for (const l of lines) {
      if (!parseFloat(l?.amount) || parseFloat(l?.amount) <= 0) { setError(t('subInvoices.addModal.errorAmount')); return; }
      if (l?.lineType === 'cost_code' && !l?.projectCostId) { setError(t('subInvoices.addModal.errorCostCode')); return; }
      if (l?.lineType === 'extra_credit' && !l?.extraCreditId) { setError(t('subInvoices.addModal.errorExtra')); return; }
    }
    setSaving(true);
    try {
      // Always write subcontractId for unified aggregation
      // contractId is set only for subcontract type (for backward compat)
      const selectedSubcontract = subcontracts?.find(s => s?.id === form?.subcontractId);
      const payload = {
        ...form,
        projectId,
        subcontractId: form?.subcontractId || null,
        contractId: invoiceType === 'subcontract' ? form?.subcontractId : null,
        extraId: invoiceType === 'extra' ? form?.extraId : null,
        creditId: invoiceType === 'credit' ? form?.creditId : null,
        paymentRequestId: form?.paymentRequestId || null,
        subcontractorId: selectedSubcontract?.subcontractorId || null,
        // Memes cles que la modale de la boite de reception
        // (InvoiceForm.jsx:829-834) : les deux parlent la meme langue au
        // service. Sans elles, update() sautait chaque colonne grace a sa garde
        // `!== undefined`, et l'en-tete restait fige a sa valeur de creation.
        subtotalAmount: subtotal,
        tpsAmount: tps,
        tvqAmount: tvq,
        totalAmount: grandTotal,
        // Envoyable depuis que la case est initialisee depuis la facture :
        // enregistrer sans y toucher renvoie la valeur stockee, donc ne la
        // change pas.
        taxApplied: applyTax,
        // retentionIncluded reste absent : cette modale n'a ni etat ni case a
        // cocher pour lui. La garde `!== undefined` du service laisse la
        // valeur stockee intacte.
      };
      if (isEdit) {
        const updated = await subInvoiceService?.update(initialData?.id, payload, lines);
        onSave(updated);
      } else {
        const inv = await subInvoiceService?.create(payload, lines);
        onSave(inv);
      }
      onClose();
    } catch (err) {
      // Le message technique reste dans la console, la ou il sert au
      // diagnostic ; l'ecran montre la phrase traduite. `err.message` n'est
      // jamais vide, donc le repli i18n n'etait jamais atteint et l'utilisateur
      // lisait le texte brut de Postgres.
      console.error('[SubInvoicesPage] save failed:', err);
      setError(t('subInvoices.addModal.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const INVOICE_TYPES = [
    { value: 'subcontract', label: t('subInvoices.invoiceType.subcontract') },
    { value: 'extra', label: t('subInvoices.invoiceType.extra') },
    { value: 'credit', label: t('subInvoices.invoiceType.credit') }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">
            {isEdit ? t('invoice.form.editTitle') : t('invoice.form.title')}
          </h2>
          <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600"><Icon name="X" size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('invoice.form.sectionDetails')}</h3>

          {/* Invoice Type — radio segmented control */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">{t('invoice.form.invoiceType')} <span className="text-red-500">*</span></label>
            <div className="flex gap-0 border border-gray-200 rounded-lg overflow-hidden">
              {INVOICE_TYPES?.map((type) => (
                <button
                  key={type?.value}
                  type="button"
                  onClick={() => setInvoiceType(type?.value)}
                  className={`flex-1 py-2 text-xs font-medium transition-colors ${
                    invoiceType === type?.value
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {type?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Conditional reference selector */}
          {/* Subcontract selector — shown for ALL invoice types */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoice.form.selectSubcontract')} <span className="text-red-500">*</span></label>
            <SubcontractCombobox
              subcontracts={subcontracts || []}
              value={form?.subcontractId || null}
              onChange={val => setForm(p => ({ ...p, subcontractId: val || '', contractId: invoiceType === 'subcontract' ? val || '' : p?.contractId }))}
              placeholder="Search subcontract..."
              size="md"
            />
          </div>
          {invoiceType === 'extra' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoice.form.extra')} <span className="text-red-500">*</span></label>
              <SearchableSelect
                options={extraOptions?.map(e => ({ value: e?.id, label: e?.title, code: e?.coNumber }))}
                value={form?.extraId || null}
                onChange={val => setField('extraId', val || '')}
                placeholder="Search extras..."
                size="md"
              />
            </div>
          )}
          {invoiceType === 'credit' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoice.form.credit')} <span className="text-red-500">*</span></label>
              <SearchableSelect
                options={creditOptions?.map(e => ({ value: e?.id, label: e?.title, code: e?.coNumber }))}
                value={form?.creditId || null}
                onChange={val => setField('creditId', val || '')}
                placeholder="Search credits..."
                size="md"
              />
            </div>
          )}

          {/* Invoice Number + Payment Request */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoice.form.invoiceNumber')}</label>
              <input type="text" value={form?.invoiceNumber} onChange={e => setField('invoiceNumber', e?.target?.value)}
                placeholder="e.g. INV-2024-001"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoice.form.paymentRequest')}</label>
              <select
                value={form?.paymentRequestId || ''}
                onChange={e => setField('paymentRequestId', e?.target?.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
              >
                <option value="">— None —</option>
                {(paymentRequests || [])?.map(pr => (
                  <option key={pr?.id} value={pr?.id}>
                    {dpOptionLabel(pr, t, 'invoice.form')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Invoice Date */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoice.form.invoiceDate')}</label>
              <input type="date" value={form?.invoiceDate} onChange={e => setField('invoiceDate', e?.target?.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            </div>
          </div>

          {/* Invoice Lines */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('invoice.form.invoiceLines')}</label>
              <button onClick={addLine} className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-medium">
                <Icon name="Plus" size={12} /> {t('invoice.form.addLine')}
              </button>
            </div>
            <div className="space-y-2">
              {lines?.map((line, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-3 bg-gray-50 space-y-2">
                  <div className="flex items-center gap-2">
                    <SearchableSelect
                      options={[
                        { value: 'cost_code', label: t('subInvoices.addModal.lineTypeCostCode') },
                        { value: 'extra_credit', label: t('subInvoices.addModal.lineTypeExtra') }
                      ]}
                      value={line?.lineType}
                      onChange={val => setLine(i, 'lineType', val || 'cost_code')}
                      placeholder="Select type..."
                      size="sm"
                    />
                    {lines?.length > 1 && (
                      <button onClick={() => removeLine(i)} className="ml-auto p-1 rounded text-gray-400 hover:text-red-500"><Icon name="Trash2" size={12} /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-[2fr_1fr] gap-3">
                    <div>
                      {line?.lineType === 'cost_code' ? (
                        <SearchableSelect
                          options={costCodes?.map(c => ({ value: c?.id, label: `${c?.costCode} – ${c?.description}`, searchText: `${c?.costCode} ${c?.description}`?.toLowerCase() }))}
                          value={line?.projectCostId || null}
                          onChange={val => setLine(i, 'projectCostId', val || '')}
                          placeholder={t('subInvoices.addModal.selectCostCode')}
                          size="sm"
                        />
                      ) : (
                        <SearchableSelect
                          options={extras?.map(ex => ({ value: ex?.id, label: ex?.title, code: ex?.coNumber }))}
                          value={line?.extraCreditId || null}
                          onChange={val => setLine(i, 'extraCreditId', val || '')}
                          placeholder={t('subInvoices.addModal.selectExtra')}
                          size="sm"
                        />
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={
                          amountDisplays?.[i] === 'editing'
                            ? line?.amount
                            : line?.amount
                              ? fmtCAD(line?.amount)
                              : ''
                        }
                        onFocus={() => handleAmountFocus(i)}
                        onBlur={() => handleAmountBlur(i, line?.amount)}
                        onChange={e => setLine(i, 'amount', e?.target?.value)}
                        placeholder="0.00"
                        className="w-full pr-8 text-right text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">$</span>
                    </div>
                  </div>
                  {/* Subcontract selector — only for cost_code lines with matching L&M subcontracts */}
                  {line?.lineType === 'cost_code' && getLaborMaterialSubcontracts(line?.projectCostId)?.length > 0 && (
                    <div>
                      <label className="block text-[10px] font-medium text-gray-500 mb-1">Subcontract <span className="text-gray-400">(optional)</span></label>
                      <SearchableSelect
                        options={[
                          { value: '', label: '— None —' },
                          ...getLaborMaterialSubcontracts(line?.projectCostId)?.map(s => ({
                            value: s?.id,
                            label: s?.name || s?.company || s?.id
                          }))
                        ]}
                        value={line?.subcontractId || null}
                        onChange={val => setLine(i, 'subcontractId', val || '')}
                        placeholder="Select subcontract (optional)..."
                        size="sm"
                      />
                    </div>
                  )}
                  <input type="text" value={line?.description} onChange={e => setLine(i, 'description', e?.target?.value)}
                    placeholder={t('invoice.form.description')}
                    className="w-full mt-2 text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                </div>
              ))}
            </div>
          </div>

          {/* Tax summary — replaces old Invoice Total */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">{t('invoice.form.subtotal')}</span>
              <span className="font-medium text-gray-900">{fmtCAD(subtotal)}</span>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="applyTax"
                checked={applyTax}
                onChange={e => setApplyTax(e?.target?.checked)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
              />
              <label htmlFor="applyTax" className="text-xs text-gray-600 cursor-pointer">{t('invoice.form.applyTaxes')}</label>
            </div>
            {applyTax && (
              <>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">TPS (5%)</span>
                  <span className="font-medium text-gray-900">{fmtCAD(tps)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">TVQ (9.975%)</span>
                  <span className="font-medium text-gray-900">{fmtCAD(tvq)}</span>
                </div>
              </>
            )}
            <div className="border-t border-gray-200 pt-1.5 mt-1 flex items-center justify-between text-xs">
              <span className="font-semibold text-gray-700">{t('invoice.form.grandTotal')}</span>
              <span className="font-bold text-gray-900">{fmtCAD(grandTotal)}</span>
            </div>
          </div>

          {/* Notes — moved below invoice lines */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('invoice.form.sectionNotes')}</h3>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('subInvoices.addModal.notesLabel')}</label>
            <textarea value={form?.notes} onChange={e => setField('notes', e?.target?.value)} rows={2}
              placeholder={t('subInvoices.addModal.notesPlaceholder')}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">{t('invoice.form.cancel')}</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-xl transition-colors disabled:opacity-50">
            <Icon name="Save" size={14} />
            {saving
              ? t('subInvoices.addModal.saving')
              : isEdit
                ? t('subInvoices.addModal.saveChanges')
                : t('invoice.form.save')
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Approve Modal (pending_approval -> approved_to_pay) ──────
const ApproveModal = ({ invoice, onClose, onApproved }) => {
  const { t } = useTranslation();
  const [holdback, setHoldback] = useState(false);
  const [paymentNote, setPaymentNote] = useState('');
  const [clientPaymentRequestNumber, setClientPaymentRequestNumber] = useState(invoice?.paymentRequest?.number ?? '');
  const [clientPaymentRequestNumberError, setClientPaymentRequestNumberError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const invoiceTotal = invoice?.invoiceTotalWithTax || 0;
  const [approvedAmount, setApprovedAmount] = useState(parseFloat(invoiceTotal?.toFixed(2)));
  const retainagePercent = holdback ? 10 : 0;
  const retainageAmount = approvedAmount * (retainagePercent / 100);
  const netPayable = approvedAmount - retainageAmount;

  // ─── Production Validation State ──────────────────────────────
  const [estimatedPctComplete, setEstimatedPctComplete] = useState('');
  const [validationNote, setValidationNote] = useState('');
  const [validationAcknowledged, setValidationAcknowledged] = useState(false);
  // Facture rattachee a une DP que le client n'a pas encore payee.
  const dpNotPaid = Boolean(invoice?.paymentRequest) && invoice?.paymentRequest?.status !== 'paid';
  const [validationNoteError, setValidationNoteError] = useState('');

  // Compute linked budget sum from cost_code lines only (ignore extra_credit lines per v1 spec)
  const linkedBudgetSum = useMemo(() => {
    const costCodeLines = (invoice?.lines || [])?.filter(l => l?.lineType === 'cost_code' && l?.projectCost?.budget != null);
    if (costCodeLines?.length === 0) return null;
    return costCodeLines?.reduce((sum, l) => sum + (parseFloat(l?.projectCost?.budget) || 0), 0);
  }, [invoice?.lines]);

  const pctValue = estimatedPctComplete !== '' ? parseFloat(estimatedPctComplete) : null;
  const validatedAmount = (linkedBudgetSum != null && pctValue != null) ? linkedBudgetSum * pctValue / 100 : null;
  const overpaymentThreshold = validatedAmount != null ? validatedAmount * 1.05 : null;
  const showWarning = ENABLE_PRODUCTION_VALIDATION_WARNING &&
    pctValue != null &&
    validatedAmount != null &&
    approvedAmount > (overpaymentThreshold ?? Infinity);

  const overpaymentPct = (showWarning && validatedAmount > 0)
    ? (((approvedAmount - validatedAmount) / validatedAmount) * 100)?.toFixed(1)
    : null;

  // Re-acknowledgment required if user changes approved_amount or estimatedPctComplete after prior ack
  const [prevApprovedAmount, setPrevApprovedAmount] = useState(approvedAmount);
  const [prevPct, setPrevPct] = useState(estimatedPctComplete);
  useEffect(() => {
    if (validationAcknowledged && (approvedAmount !== prevApprovedAmount || estimatedPctComplete !== prevPct)) {
      setValidationAcknowledged(false);
      setPrevApprovedAmount(approvedAmount);
      setPrevPct(estimatedPctComplete);
    }
  }, [approvedAmount, estimatedPctComplete]);

  const handleApprove = async () => {
    setError('');
    setClientPaymentRequestNumberError('');
    setValidationNoteError('');

    // Validate approvedAmount
    if (!approvedAmount || approvedAmount <= 0) {
      setError(t('subInvoices.approveModal.errorApprove') || 'Approved amount must be greater than 0.');
      return;
    }
    if (approvedAmount > invoiceTotal + 0.01) {
      setError(`Approved amount cannot exceed invoice total (${fmt(invoiceTotal)}).`);
      return;
    }
    // Validate clientPaymentRequestNumber
    const cprnVal = String(clientPaymentRequestNumber ?? '')?.trim();
    if (!cprnVal) {
      setClientPaymentRequestNumberError(t('subInvoices.approveModal.validation.requiredClientPaymentRequestNumber'));
      return;
    }
    const cprnInt = parseInt(cprnVal, 10);
    if (!Number.isInteger(cprnInt) || cprnInt <= 0 || String(cprnInt) !== cprnVal) {
      setClientPaymentRequestNumberError(t('subInvoices.approveModal.validation.requiredClientPaymentRequestNumber'));
      return;
    }

    // Production validation guardrail checks
    if (ENABLE_PRODUCTION_VALIDATION_WARNING && showWarning) {
      if (!String(validationNote ?? '')?.trim()) {
        setValidationNoteError(t('subInvoices.approveModal.errorValidationNote'));
        return;
      }
      if (!validationAcknowledged) {
        setError(t('subInvoices.approveModal.errorAcknowledge'));
        return;
      }
    }

    setSaving(true);
    try {
      // Build validation metadata to pass to service
      const validationMeta = ENABLE_PRODUCTION_VALIDATION_WARNING ? {
        estimatedPercentComplete: pctValue,
        validatedAmount: validatedAmount,
        overpaymentRisk: pctValue != null ? showWarning : null,
        validationNote: String(validationNote ?? '')?.trim() || null,
        validationAcknowledged: showWarning ? validationAcknowledged : false,
      } : {};

      const result = await subInvoiceService?.approve(
        invoice?.id,
        { approvedAmount, retainagePercent, paymentNote, clientPaymentRequestNumber: cprnInt, ...validationMeta },
        invoice?.lines || []
      );
      if (result?.success === false) {
        setError((result?.errors || [])?.join(' '));
        setSaving(false);
        return;
      }
      onApproved(result?.invoice || result);
      onClose();
    } catch (err) {
      setError(err?.message || t('subInvoices.approveModal.errorApprove'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">{t('subInvoices.approveModal.title')}</h2>
          <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600"><Icon name="X" size={16} /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('subInvoices.approveModal.invoiceNumber')}</span>
              <span className="font-medium text-gray-900">{invoice?.invoiceNumber || '—'}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('subInvoices.table.subcontractor')}</span>
              <span className="font-medium text-gray-900">{invoice?.subcontractor?.companyName || '—'}</span>
            </div>
            <div className="flex justify-between text-xs border-t border-gray-200 pt-1">
              <span className="font-semibold text-gray-700">{t('subInvoices.approveModal.invoiceTotal')}</span>
              <span className="font-bold text-gray-900">{fmt(invoiceTotal)}</span>
            </div>
          </div>
          {/* Client Payment Request # — required */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('subInvoices.approveModal.clientPaymentRequestNumber')} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={clientPaymentRequestNumber}
              onChange={e => { setClientPaymentRequestNumber(e?.target?.value); setClientPaymentRequestNumberError(''); }}
              placeholder={t('subInvoices.approveModal.enterClientPaymentRequestNumber')}
              className={`w-full text-xs border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                clientPaymentRequestNumberError ? 'border-red-400 bg-red-50' : 'border-gray-200'
              }`}
            />
            {clientPaymentRequestNumberError && (
              <p className="text-[11px] text-red-600 mt-1">{clientPaymentRequestNumberError}</p>
            )}
          </div>
          {/* Approved Amount — editable */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Approved Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={approvedAmount}
              onChange={e => setApprovedAmount(parseFloat((parseFloat(e?.target?.value) || 0)?.toFixed(2)))}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          {/* ─── Production Validation Section ─────────────────────── */}
          {ENABLE_PRODUCTION_VALIDATION_WARNING && (
            <div className="border border-gray-200 rounded-lg p-3 space-y-3">
              <div className="flex items-center gap-2">
                <Icon name="BarChart2" size={13} className="text-gray-400" />
                <span className="text-xs font-semibold text-gray-700">Production Validation</span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Estimated % complete for this cost code
                  <span className="text-gray-400 font-normal ml-1">(optional, 0–100)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={estimatedPctComplete}
                  onChange={e => {
                    const raw = e?.target?.value;
                    if (raw === '' || raw === null || raw === undefined) {
                      setEstimatedPctComplete('');
                      return;
                    }
                    const num = parseFloat(raw);
                    if (isNaN(num)) {
                      setEstimatedPctComplete('');
                    } else if (num > 100) {
                      setEstimatedPctComplete(100);
                    } else if (num < 0) {
                      setEstimatedPctComplete(0);
                    } else {
                      setEstimatedPctComplete(raw);
                    }
                  }}
                  placeholder="e.g. 65"
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* No budget linked */}
              {estimatedPctComplete !== '' && linkedBudgetSum === null && (
                <p className="text-[11px] text-gray-500 italic">No linked budget available for validation</p>
              )}

              {/* Earned value display */}
              {validatedAmount != null && (
                <div className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-700">
                  Validated earned value:{' '}
                  <span className="font-semibold text-gray-900">{fmt(validatedAmount)}</span>
                  <span className="text-gray-400 ml-1">
                    ({fmt(linkedBudgetSum)} × {pctValue}%)
                  </span>
                </div>
              )}

              {/* No % entered — subtle info */}
              {estimatedPctComplete === '' && (
                <p className="text-[11px] text-gray-400 italic">No production validation entered</p>
              )}

              {/* ⚠️ Overpayment Warning Banner */}
              {showWarning && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="text-yellow-600 text-sm mt-0.5">⚠️</span>
                    <div>
                      <p className="text-xs font-semibold text-yellow-800">
                        This approval exceeds estimated earned value by {overpaymentPct}%. Possible overpayment risk.
                      </p>
                    </div>
                  </div>

                  {/* Three-number comparison */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-white rounded-md px-2 py-2 border border-yellow-200">
                      <p className="text-[10px] text-gray-500 leading-tight mb-0.5">Sum of Linked Budgets</p>
                      <p className="text-xs font-bold text-gray-900">{fmt(linkedBudgetSum)}</p>
                    </div>
                    <div className="bg-white rounded-md px-2 py-2 border border-yellow-200">
                      <p className="text-[10px] text-gray-500 leading-tight mb-0.5">Earned Value at {pctValue}%</p>
                      <p className="text-xs font-bold text-blue-700">{fmt(validatedAmount)}</p>
                    </div>
                    <div className="bg-white rounded-md px-2 py-2 border border-red-200">
                      <p className="text-[10px] text-gray-500 leading-tight mb-0.5">Approved Amount</p>
                      <p className="text-xs font-bold text-red-700">{fmt(approvedAmount)}</p>
                    </div>
                  </div>

                  {/* Validation note — required when warning is shown */}
                  <div>
                    <label className="block text-xs font-medium text-yellow-800 mb-1">
                      Explain why this approval is valid <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={validationNote}
                      onChange={e => { setValidationNote(e?.target?.value); setValidationNoteError(''); }}
                      placeholder="e.g. mobilization, stored materials, front-loaded SOV"
                      className={`w-full text-xs border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                        validationNoteError ? 'border-red-400 bg-red-50' : 'border-yellow-300 bg-white'
                      }`}
                    />
                    {validationNoteError && (
                      <p className="text-[11px] text-red-600 mt-1">{validationNoteError}</p>
                    )}
                  </div>

                  {/* Acknowledgment checkbox */}
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="validation-ack"
                      checked={validationAcknowledged}
                      onChange={e => setValidationAcknowledged(e?.target?.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-yellow-400 text-yellow-600 focus:ring-yellow-500 cursor-pointer"
                    />
                    <label htmlFor="validation-ack" className="text-xs text-yellow-800 cursor-pointer leading-relaxed">
                      I acknowledge the overpayment risk and confirm this approval is intentional.
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* ─── End Production Validation ─────────────────────────── */}

          {/* La DP de cette facture n'est pas encore payee par le client : avertissement seulement, l'approbation reste possible. */}
          {dpNotPaid && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-start gap-2">
              <span className="text-yellow-600 text-sm mt-0.5">⚠️</span>
              <p className="text-xs font-semibold text-yellow-800">
                {t('subInvoices.approveModal.dpNotPaid', { number: invoice?.paymentRequest?.number })}
              </p>
            </div>
          )}

          <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
            <input
              type="checkbox"
              id="holdback"
              checked={holdback}
              onChange={e => setHoldback(e?.target?.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
            />
            <label htmlFor="holdback" className="text-xs font-medium text-gray-700 cursor-pointer">
              {t('subInvoices.approveModal.holdbackLabel')}
            </label>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">{t('subInvoices.approveModal.approvedAmount')}</span>
              <span className="font-medium text-gray-900">{fmt(approvedAmount)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">{t('subInvoices.approveModal.retainage')} ({retainagePercent}%)</span>
              <span className={`font-medium ${holdback ? 'text-red-600' : 'text-gray-400'}`}>
                {holdback ? `−${fmt(retainageAmount)}` : '—'}
              </span>
            </div>
            <div className="flex justify-between text-xs border-t border-blue-100 pt-1">
              <span className="font-semibold text-gray-700">{t('subInvoices.approveModal.netPayable')}</span>
              <span className="font-bold text-green-700">{fmt(netPayable)}</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('subInvoices.approveModal.paymentNoteLabel')}</label>
            <input type="text" value={paymentNote} onChange={e => setPaymentNote(e?.target?.value)}
              placeholder="e.g. Approved per PM review"
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">{t('subInvoices.approveModal.cancel')}</button>
          <button
            onClick={handleApprove}
            disabled={saving}
            className="px-4 py2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving
              ? t('subInvoices.approveModal.approving')
              : dpNotPaid
                ? t('subInvoices.approveModal.approveAnyway')
                : t('subInvoices.approveModal.approve')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Record Payment Modal (requires Check #) ──────────────────
const RecordPaymentModal = ({ invoice, onClose, onPaid }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ amountPaid: '', paidDate: '', method: '', reference: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const latestApproval = invoice?.approvals?.[0] || null;
  const invoiceTotalWithTax = invoice?.invoiceTotalWithTax || 0;
  const grossApproved = latestApproval ? (parseFloat(latestApproval?.approvedAmount) || 0) : 0;
  const retainagePercent = latestApproval ? (parseFloat(latestApproval?.retainagePercent) || 0) : 0;
  const retainageAmount = latestApproval ? (parseFloat(latestApproval?.retainageAmount) || 0) : 0;
  const netPayable = invoice?.netPayable || 0;
  const totalPaid = invoice?.totalPaid || 0;
  // Une fois la retenue libérée, le plafond passe du net au brut approuvé
  // (la retenue elle-même devient payable) ; sinon, plafond = net à payer.
  const retentionReleasedAt = latestApproval?.retentionReleasedAt || null;
  const paymentCeiling = retentionReleasedAt ? grossApproved : netPayable;
  const remainingApproved = Math.max(0, paymentCeiling - totalPaid);
  // Solde = plafond payable − déjà payé (cohérent avec « Approuvé restant »)
  const solde = Math.max(0, paymentCeiling - totalPaid);

  const handleSave = async () => {
    setError('');
    if (!parseFloat(form?.amountPaid) || parseFloat(form?.amountPaid) <= 0) {
      setError(t('subInvoices.paymentModal.errorAmount'));
      return;
    }
    if (!String(form?.reference ?? '')?.trim()) {
      setError(t('subInvoices.paymentModal.errorReference'));
      return;
    }
    // UI guard: payment cannot exceed remaining payable amount
    if (paymentCeiling > 0 && parseFloat(form?.amountPaid) > remainingApproved + 0.01) {
      setError(t('subInvoices.paymentModal.errorExceedsApproved'));
      return;
    }
    setSaving(true);
    try {
      const updated = await subInvoiceService?.recordPayment(invoice?.id, form, netPayable);
      onPaid(updated);
      onClose();
    } catch (err) {
      setError(err?.message || t('subInvoices.paymentModal.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">{t('subInvoices.paymentModal.title')}</h2>
          <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600"><Icon name="X" size={16} /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('subInvoices.approveModal.invoiceNumber')}</span>
              <span className="font-medium text-gray-900">{invoice?.invoiceNumber || '—'}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('subInvoices.paymentModal.invoiceTotalTaxIn')}</span>
              <span className="font-semibold text-gray-900">{fmt(invoiceTotalWithTax)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('subInvoices.paymentModal.approvedAmount')}</span>
              <span className="font-medium text-gray-700">{fmt(grossApproved)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('subInvoices.approveModal.retainage')} ({retainagePercent}%)</span>
              <span className="font-medium text-red-600">{retainagePercent > 0 ? `−${fmt(retainageAmount)}` : '—'}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('subInvoices.approveModal.netPayable')}</span>
              <span className="font-medium text-gray-700">{fmt(netPayable)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('subInvoices.paymentModal.alreadyPaid')}</span>
              <span className="font-medium text-gray-700">{fmt(totalPaid)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('subInvoices.paymentModal.remainingApproved')}</span>
              <span className={`font-semibold ${remainingApproved > 0 ? 'text-orange-600' : 'text-green-600'}`}>{fmt(remainingApproved)}</span>
            </div>
            <div className="flex justify-between text-xs border-t border-gray-200 pt-1">
              <span className="font-semibold text-gray-700">{t('subInvoices.paymentModal.solde')}</span>
              <span className={`font-bold ${solde > 0 ? 'text-red-500' : 'text-green-600'}`}>{fmt(solde)}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('subInvoices.paymentModal.amountPaidLabel')} <span className="text-red-500">*</span>
              </label>
              <input type="number" min="0" step="0.01" value={form?.amountPaid} onChange={e => setField('amountPaid', e?.target?.value)}
                placeholder="0.00"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('subInvoices.paymentModal.paidDateLabel')}</label>
              <input type="date" value={form?.paidDate} onChange={e => setField('paidDate', e?.target?.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('subInvoices.paymentModal.methodLabel')}</label>
              <SearchableSelect
                options={[
                  { value: 'check', label: 'Check' },
                  { value: 'eft', label: 'EFT' },
                  { value: 'wire', label: 'Wire' },
                  { value: 'other', label: 'Other' }
                ]}
                value={form?.method || null}
                onChange={val => setField('method', val || '')}
                placeholder="Select..."
                allowNone={true}
                size="md"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('subInvoices.paymentModal.referenceLabel')} <span className="text-red-500">*</span>
              </label>
              <input type="text" value={form?.reference} onChange={e => setField('reference', e?.target?.value)}
                placeholder={t('subInvoices.paymentModal.referencePlaceholder')}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">{t('subInvoices.paymentModal.cancel')}</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50">
            {saving ? t('subInvoices.paymentModal.saving') : t('subInvoices.paymentModal.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Sort Icon (matches ExtrasTable style) ───────────────────
const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig?.key !== column) {
    return <Icon name="ChevronsUpDown" size={12} className="text-gray-400" />;
  }
  return sortConfig?.direction === 'asc'
    ? <Icon name="ChevronUp" size={12} className="text-primary" />
    : <Icon name="ChevronDown" size={12} className="text-primary" />;
};

// ─── Filtre de statut cliquable (dropdown a pastilles) — onglet Factures ──
// Libelles depuis getStatusConfig ; couleur de pastille via
// statusVariant -> BADGE_VARIANTS, coherente avec les badges de ligne.
const StatusFilterDropdown = ({ value, onChange, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const cfg = getStatusConfig(t);
  const options = [
    { value: 'all', label: t('subInvoices.filters.allStatuses'), dot: null },
    ...Object.keys(cfg).map((status) => ({
      value: status,
      label: cfg[status].label,
      dot: BADGE_VARIANTS?.[statusVariant('subInvoices', status)]?.dot,
    })),
  ];
  const current = options.find((o) => o.value === value) || options[0];
  return (
    <div className="relative">
      <button ref={ref} type="button" onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-200 rounded-md bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors">
        {current.dot && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: current.dot }} />}
        <span className="text-gray-700">{current.label}</span>
        <Icon name="ChevronDown" size={12} className="text-gray-400" />
      </button>
      <PortalDropdown anchorRef={ref} open={open} onClose={() => setOpen(false)} align="left" width={200}
        items={options.map((o) => ({
          label: o.label,
          dot: o.dot || undefined,
          active: o.value === value,
          onClick: () => onChange(o.value),
        }))} />
    </div>
  );
};

// ─── TAB 1: Invoices (status === 'received') ────────────────────────────
const InvoicesTab = ({ invoices, onSubmitForApproval, onRequestRetentionRelease, onDelete, onEdit, projectId, selectedIds, onToggleSelect, onToggleSelectAll, sortConfig, onSortChange }) => {
  const { t, language } = useTranslation();
  // Show ALL invoices — no status filter
  const allInvoices = invoices;

  const canDelete = (inv) => !(inv?.approvals?.length) && !(inv?.payments?.length);

  const [documentsInvoice, setDocumentsInvoice] = useState(null);
  const [docCounts, setDocCounts] = useState({});

  const handleSort = (key) => {
    onSortChange(prev =>
      prev?.key === key
        ? { key, direction: prev?.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const sortedInvoices = React.useMemo(() => {
    const STATUS_ORDER = { received: 0, pending_approval: 1, approved_to_pay: 2, paid: 3 };
    if (!sortConfig?.key) {
      return [...(allInvoices || [])]?.sort((a, b) => {
        const aOrder = STATUS_ORDER?.[a?.status] ?? 99;
        const bOrder = STATUS_ORDER?.[b?.status] ?? 99;
        return aOrder - bOrder;
      });
    }
    return [...(allInvoices || [])]?.sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig?.key) {
        case 'invoiceDate':
          aVal = a?.invoiceDate || '';
          bVal = b?.invoiceDate || '';
          break;
        case 'invoiceNumber':
          aVal = a?.invoiceNumber || '';
          bVal = b?.invoiceNumber || '';
          break;
        case 'subcontractor':
          aVal = a?.subcontractor?.companyName || '';
          bVal = b?.subcontractor?.companyName || '';
          break;
        case 'invoiceType':
          aVal = a?.invoiceType || '';
          bVal = b?.invoiceType || '';
          break;
        case 'invoiceTotal':
          aVal = parseFloat(a?.invoiceTotalWithTax) || 0;
          bVal = parseFloat(b?.invoiceTotalWithTax) || 0;
          break;
        case 'balance':
          aVal = parseFloat(a?.balance) || 0;
          bVal = parseFloat(b?.balance) || 0;
          break;
        case 'status':
          aVal = a?.status || '';
          bVal = b?.status || '';
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [allInvoices, sortConfig]);

  const SortableHeader = ({ column, label, align = 'left' }) => (
    <th
      className={`text-${align} text-xs font-medium text-gray-500 px-4 py-3 cursor-pointer select-none hover:bg-gray-100 transition-colors whitespace-nowrap`}
      onClick={() => handleSort(column)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''}`}>
        <span>{label}</span>
        <SortIcon column={column} sortConfig={sortConfig} />
      </div>
    </th>
  );

  const allVisibleIds = sortedInvoices?.map(inv => inv?.id) || [];
  const allSelected = allVisibleIds?.length > 0 && allVisibleIds?.every(id => selectedIds?.has(id));
  const someSelected = allVisibleIds?.some(id => selectedIds?.has(id)) && !allSelected;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
      <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
        {allInvoices?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="FileX" size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">{t('subInvoices.empty.noInvoices')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('subInvoices.empty.clickToAdd')}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onChange={() => onToggleSelectAll(allVisibleIds)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                  />
                </th>
                <SortableHeader column="invoiceDate" label={t('subInvoices.table.date') || 'Date'} />
                <SortableHeader column="invoiceNumber" label={t('subInvoices.table.invoiceNumber')} />
                <SortableHeader column="subcontractor" label={t('subInvoices.table.subcontractor')} />
                <SortableHeader column="invoiceType" label={t('subInvoices.table.invoiceType')} />
                <SortableHeader column="invoiceTotal" label={t('subInvoices.table.amountTaxIn') || 'Amount (Tax In)'} align="right" />
                <SortableHeader column="balance" label={t('subInvoices.table.balance') || 'Balance'} align="right" />
                <th className="text-center text-xs font-medium text-gray-500 px-4 py-3 whitespace-nowrap">DP</th>
                <SortableHeader column="status" label={t('subInvoices.table.status')} align="center" />
                <th className="text-right text-xs font-medium text-gray-500 px-4 py-3 whitespace-nowrap">{t('subInvoices.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedInvoices?.map((inv, index) => (
                <tr
                  key={inv?.id}
                  className={`transition-colors ${selectedIds?.has(inv?.id) ? 'bg-orange-50 hover:bg-orange-100/70' : 'hover:bg-gray-50/60'}`}
                >
                  <td className="px-4 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds?.has(inv?.id) || false}
                      onChange={() => onToggleSelect(inv?.id)}
                      onClick={e => e?.stopPropagation()}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{fmtDate(inv?.invoiceDate, language)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-gray-700">{inv?.invoiceNumber || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-medium text-gray-900">{inv?.subcontractor?.companyName || '—'}</p>
                    {inv?.subcontractor?.trade && (
                      <p className="text-[11px] text-gray-400">{inv?.subcontractor?.trade}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <InvoiceTypeBadge invoiceType={inv?.invoiceType} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-xs font-semibold text-gray-900">{fmt(inv?.invoiceTotalWithTax)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-xs font-semibold ${(inv?.balance ?? 0) > 0 ? 'text-red-500' : 'text-green-600'}`}>
                      {fmt(inv?.balance ?? 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {inv?.paymentRequest ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 whitespace-nowrap">
                        DP{inv?.paymentRequest?.number}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={inv?.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-end justify-end gap-2">
                      {/* Submit for approval — always visible */}
                      {inv?.status === 'received' && (
                        <InstantTooltip label={t('subInvoices.actions.submitForApproval')}>
                          <button
                            onClick={() => onSubmitForApproval(inv)}
                            className="p-1 rounded text-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            <Icon name="Send" size={16} className="w-4 h-4" />
                          </button>
                        </InstantTooltip>
                      )}
                      {/* Payer la retenue — net entièrement payé, retenue en attente de libération */}
                      {inv?.status === 'partially_paid'
                        && (parseFloat(inv?.latestApproval?.retainageAmount) || 0) > 0
                        && (inv?.totalPaid ?? 0) >= ((inv?.netPayable ?? 0) - 0.01)
                        && !inv?.latestApproval?.retentionReleaseRequested
                        && !inv?.latestApproval?.retentionReleasedAt && (
                        <InstantTooltip label={t('subInvoices.actions.payRetention')}>
                          <button
                            onClick={() => onRequestRetentionRelease(inv)}
                            className="p-1 rounded text-orange-500 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                          >
                            <Icon name="Coins" size={16} className="w-4 h-4" />
                          </button>
                        </InstantTooltip>
                      )}
                      {/* Document icon + count badge */}
                      <DocumentIconButton
                        invoice={inv}
                        projectId={projectId}
                        onClick={() => setDocumentsInvoice(inv)}
                        externalCount={docCounts?.[inv?.id]}
                        onCountFetched={(id, count) => setDocCounts(prev => ({ ...prev, [id]: count }))}
                      />
                      {/* 3-dot menu */}
                      <InvoiceActionsMenu
                        invoice={inv}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        canDelete={canDelete(inv)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Documents Modal */}
      {documentsInvoice && (
        <DocumentsModal
          isOpen={!!documentsInvoice}
          onClose={() => setDocumentsInvoice(null)}
          entityId={documentsInvoice?.id}
          entityType="sub_invoices"
          subtitle={documentsInvoice?.invoiceNumber || documentsInvoice?.id}
          onFilesChanged={(count) => {
            setDocCounts(prev => ({ ...prev, [documentsInvoice?.id]: count }));
          }}
        />
      )}
    </div>
  );
};

// ─── TAB 2: Invoice Approval (status === 'pending_approval') ──
const InvoiceApprovalTab = ({ invoices, subcontracts, onApprove, projectId, selectedIds, onToggleSelect, onToggleSelectAll }) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  // Filter: only pending_approval invoices, optionally by subcontractor
  const [selectedApprovalSubcontractId, setSelectedApprovalSubcontractId] = useState('');
  const [approvalSearch, setApprovalSearch] = useState('');
  const approvalQ = approvalSearch?.trim()?.toLowerCase();
  // Réapparaissent en Approbation : (1) les factures soumises normalement, et
  // (2) les factures partiellement payées dont la retenue est demandée mais pas
  // encore libérée (aucun nouveau statut — on reste 'partially_paid').
  const isReleasePending = (inv) =>
    inv?.status === 'partially_paid' &&
    inv?.latestApproval?.retentionReleaseRequested === true &&
    !inv?.latestApproval?.retentionReleasedAt;
  const pendingInvoices = invoices?.filter(inv =>
    (inv?.status === 'pending_approval' || isReleasePending(inv)) &&
    (!selectedApprovalSubcontractId || inv?.subcontractFilterId === selectedApprovalSubcontractId) &&
    (!approvalQ ||
      (inv?.subcontractor?.companyName || '')?.toLowerCase()?.includes(approvalQ) ||
      (inv?.invoiceNumber || '')?.toLowerCase()?.includes(approvalQ))
  );

  const allVisibleIds = pendingInvoices?.map(inv => inv?.id) || [];
  const allSelected = allVisibleIds?.length > 0 && allVisibleIds?.every(id => selectedIds?.has(id));
  const someSelected = allVisibleIds?.some(id => selectedIds?.has(id)) && !allSelected;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Icon name="Search" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={approvalSearch}
              onChange={e => setApprovalSearch(e?.target?.value)}
              placeholder={t('subInvoices.filters.searchPlaceholder')}
              className="pl-7 pr-8 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-72 transition-colors"
            />
            {approvalSearch && (
              <button
                onClick={() => setApprovalSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Icon name="X" size={11} />
              </button>
            )}
          </div>
          <SubcontractCombobox subcontracts={subcontracts || []}
            value={selectedApprovalSubcontractId || null}
            onChange={val => setSelectedApprovalSubcontractId(val || '')}
            placeholder={t('subInvoices.filters.chooseSubcontract')} size="sm" className="w-64" />
        </div>
        <span className="text-xs font-medium text-gray-700">
          {pendingInvoices?.length} {pendingInvoices?.length !== 1 ? t('subInvoices.toolbar.invoices') : t('subInvoices.toolbar.invoice')} {t('subInvoices.empty.pendingApprovalSuffix')}
        </span>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
        <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
        {pendingInvoices?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="ClipboardCheck" size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">{t('subInvoices.empty.noPending')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('subInvoices.empty.pendingHint')}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onChange={() => onToggleSelectAll(allVisibleIds)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                  />
                </th>
                <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.date') || 'Date'}</th>
                <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.invoiceNumber')}</th>
                <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.subcontractor')}</th>
                <th className="text-right text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.total')}</th>
                <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.status')}</th>
                <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.invoiceType')}</th>
                <th className="text-right text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pendingInvoices?.map((inv, index) => {
                const isRelease = isReleasePending(inv);
                const retainageAmt = parseFloat(inv?.latestApproval?.retainageAmount) || 0;
                return (
                <tr
                  key={inv?.id}
                  className={`transition-colors ${selectedIds?.has(inv?.id) ? 'bg-orange-50 hover:bg-orange-100/70' : 'hover:bg-gray-50/60'}`}
                >
                  <td className="px-4 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds?.has(inv?.id) || false}
                      onChange={() => onToggleSelect(inv?.id)}
                      onClick={e => e?.stopPropagation()}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{fmtDate(inv?.invoiceDate, language)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-gray-700">{inv?.invoiceNumber || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-medium text-gray-900">{inv?.subcontractor?.companyName || '—'}</p>
                    {inv?.subcontractor?.trade && (
                      <p className="text-[11px] text-gray-400">{inv?.subcontractor?.trade}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-xs font-semibold text-gray-900">{fmt(isRelease ? retainageAmt : inv?.invoiceTotalWithTax)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {isRelease ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 whitespace-nowrap">
                        <Icon name="Coins" size={11} />
                        {t('subInvoices.status.retentionToRelease')}
                      </span>
                    ) : (
                      <StatusBadge status={inv?.status} />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <InvoiceTypeBadge invoiceType={inv?.invoiceType} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => navigate(`/project/${projectId}/sub-invoices/approve/${inv?.id}`)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border ${
                        isRelease
                          ? 'text-orange-700 bg-orange-50 hover:bg-orange-100 border-orange-200'
                          : 'text-green-700 bg-green-50 hover:bg-green-100 border-green-200'
                      }`}
                    >
                      <Icon name={isRelease ? 'Coins' : 'CheckCircle'} size={12} className={isRelease ? 'text-orange-600' : 'text-green-600'} />
                      {isRelease ? t('subInvoices.actions.releaseRetention') : t('subInvoices.actions.approveInvoice')}
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>
    </>
  );
};

// ─── TAB 3: Payments (status IN approved_to_pay, partially_paid, paid) ────────
const PaymentsTab = ({ invoices, subcontracts, paymentRequests, onRecordPayment, selectedIds, onToggleSelect, onToggleSelectAll }) => {
  const { t, language } = useTranslation();
  // Filter: approved_to_pay, partially_paid, and paid invoices, optionally by subcontractor + payment request (DP)
  const [selectedPaymentSubcontractId, setSelectedPaymentSubcontractId] = useState('');
  const [selectedPaymentDpId, setSelectedPaymentDpId] = useState('');
  const [paymentSearch, setPaymentSearch] = useState('');
  const paymentQ = paymentSearch?.trim()?.toLowerCase();

  // DP dropdown: "Tous les DP" + one option per project payment request + "Sans DP"
  const dpOptions = [
    { value: '', label: t('subInvoices.filters.allDp') },
    ...(paymentRequests || [])?.map(pr => ({ value: pr?.id, label: `DP-${pr?.number}` })),
    { value: 'none', label: t('subInvoices.filters.noDp') },
  ];

  const paymentInvoices = invoices?.filter(inv =>
    (inv?.status === 'approved_to_pay' || inv?.status === 'partially_paid' || inv?.status === 'paid') &&
    (!selectedPaymentSubcontractId || inv?.subcontractFilterId === selectedPaymentSubcontractId) &&
    (!selectedPaymentDpId
      || (selectedPaymentDpId === 'none' ? inv?.paymentRequestId == null : inv?.paymentRequestId === selectedPaymentDpId)) &&
    (!paymentQ ||
      (inv?.subcontractor?.companyName || '')?.toLowerCase()?.includes(paymentQ) ||
      (inv?.invoiceNumber || '')?.toLowerCase()?.includes(paymentQ))
  );

  const allVisibleIds = paymentInvoices?.map(inv => inv?.id) || [];
  const allSelected = allVisibleIds?.length > 0 && allVisibleIds?.every(id => selectedIds?.has(id));
  const someSelected = allVisibleIds?.some(id => selectedIds?.has(id)) && !allSelected;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Icon name="Search" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={paymentSearch}
              onChange={e => setPaymentSearch(e?.target?.value)}
              placeholder={t('subInvoices.filters.searchPlaceholder')}
              className="pl-7 pr-8 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-72 transition-colors"
            />
            {paymentSearch && (
              <button
                onClick={() => setPaymentSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Icon name="X" size={11} />
              </button>
            )}
          </div>
          <SubcontractCombobox subcontracts={subcontracts || []}
            value={selectedPaymentSubcontractId || null}
            onChange={val => setSelectedPaymentSubcontractId(val || '')}
            placeholder={t('subInvoices.filters.chooseSubcontract')} size="sm" className="w-64" />
          <FilterDropdown options={dpOptions} value={selectedPaymentDpId} onChange={setSelectedPaymentDpId} />
        </div>
        <span className="text-xs font-medium text-gray-700">
          {paymentInvoices?.length} {paymentInvoices?.length !== 1 ? t('subInvoices.toolbar.invoices') : t('subInvoices.toolbar.invoice')}
        </span>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
        <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
        {paymentInvoices?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="Banknote" size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">{t('subInvoices.empty.noPayments')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('subInvoices.empty.paymentsHint')}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onChange={() => onToggleSelectAll(allVisibleIds)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                  />
                </th>
                <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.date') || 'Date'}</th>
                <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.invoiceNumber')}</th>
                <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.subcontractor')}</th>
                <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.invoiceType')}</th>
                <th className="text-right text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.approvedAmount')}</th>
                <th className="text-right text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.retainage')}</th>
                <th className="text-right text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.netPayable')}</th>
                <th className="text-right text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.amountPaid')}</th>
                <th className="text-center text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.status')}</th>
                <th className="text-right text-xs font-semibold text-gray-700 px-4 py-3">{t('subInvoices.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paymentInvoices?.map((inv, index) => {
                const approval = inv?.approvals?.[0];
                const approvedAmt = parseFloat(approval?.approvedAmount) || inv?.invoiceTotal || 0;
                const retainageAmt = parseFloat(approval?.retainageAmount) || 0;
                const netPay = parseFloat(approval?.netPayable) || inv?.netPayable || 0;
                const totalPaid = inv?.totalPaid || 0;
                return (
                  <tr key={inv?.id} className={`transition-colors ${selectedIds?.has(inv?.id) ? 'bg-orange-50 hover:bg-orange-100/70' : 'hover:bg-gray-50/60'}`}>
                    <td className="px-4 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={selectedIds?.has(inv?.id) || false}
                        onChange={() => onToggleSelect(inv?.id)}
                        onClick={e => e?.stopPropagation()}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{fmtDate(inv?.invoiceDate, language)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-gray-700">{inv?.invoiceNumber || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-medium text-gray-900">{inv?.subcontractor?.companyName || '—'}</p>
                      {inv?.subcontractor?.trade && (
                        <p className="text-[11px] text-gray-400">{inv?.subcontractor?.trade}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <InvoiceTypeBadge invoiceType={inv?.invoiceType} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs font-semibold text-gray-900">{fmt(approvedAmt)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs font-medium text-red-600">{retainageAmt > 0 ? `−${fmt(retainageAmt)}` : '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs font-bold text-green-700">{fmt(netPay)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs font-medium text-emerald-700">{fmt(totalPaid)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={inv?.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {inv?.status === 'approved_to_pay' && (
                        <ThreeDotButton
                          invoice={inv}
                          actions={[
                            {
                              label: t('subInvoices.actions.recordPayment'),
                              icon: 'CreditCard',
                              iconClass: 'text-blue-500',
                              className: 'text-blue-700',
                              onClick: () => onRecordPayment(inv)
                            }
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>
    </>
  );
};

// ─── TAB 4: Sub Summary ───────────────────────────────────────
const SubSummaryTab = ({ subcontracts, projectId, selectedId, onSelectedChange, isActive }) => {
  const { t, language } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [invoicesOpen, setInvoicesOpen] = useState(false);

  const selected = subcontracts?.find(s => s?.id === selectedId) || null;

  useEffect(() => {
    if (!selectedId || !projectId || !isActive) {
      if (!selectedId) setSummary(null);
      return;
    }
    let cancelled = false;
    setSummaryLoading(true);
    setInvoicesOpen(false);
    // Force a fresh fetch of invoices from Supabase so contract_id is populated
    subInvoiceService?.getByProjectId(projectId)
      ?.then(() => {
        if (cancelled) return;
        return getSubcontractSummary(selectedId);
      })
      ?.then(result => {
        if (!cancelled && result) setSummary(result);
      })
      ?.catch(err => {
        console.error('[SubSummaryTab] error:', err);
        if (!cancelled) setSummary(null);
      })
      ?.finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedId, projectId, isActive]);

  const isOverbilled = summary ? summary?.remaining < 0 : false;
  const progressPct = summary ? Math.min(summary?.billingProgress * 100, 100) : 0;

  return (
    <div className="space-y-4">
      {/* Subcontract Selector */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <label className="block text-xs font-semibold text-gray-700 mb-2">{t('subInvoices.selectSubcontract')}</label>
        <SubcontractCombobox
          subcontracts={subcontracts || []}
          value={selectedId || null}
          onChange={val => onSelectedChange(val || '')}
          placeholder={t('subInvoices.filters.chooseSubcontract')}
          size="md"
          className="max-w-md"
        />
        {selected && (
          <div className="mt-2 flex flex-wrap gap-3">
            {selected?.subcontractor?.trade && (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                <Icon name="Wrench" size={11} className="text-gray-400" />
                {selected?.subcontractor?.trade}
              </span>
            )}
            {selected?.status && (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                <Icon name="Tag" size={11} className="text-gray-400" />
                {t(`subInvoices.status.${selected?.status}`)}
              </span>
            )}
          </div>
        )}
      </div>
      {/* Financial Summary */}
      {!selected ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
          <Icon name="BarChart2" size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500">{t('subInvoices.summaryTab.selectPrompt')}</p>
          <p className="text-xs text-gray-400 mt-1">{t('subInvoices.summaryTab.selectHint')}</p>
        </div>
      ) : summaryLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
          <Icon name="Loader2" size={32} className="mx-auto mb-3 text-primary animate-spin" />
          <p className="text-xs text-gray-400">{t('subInvoices.summaryTab.loading')}</p>
        </div>
      ) : summary ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="text-xs font-semibold text-gray-700">{t('subInvoices.summaryTab.title')}</span>
          </div>
          <div className="divide-y divide-gray-100">
            {/* Contract Value */}
            <div className="flex items-center justify-between px-5 py-3">
              <span className="text-xs text-gray-600">{t('subInvoices.summaryTab.contractValue')}</span>
              <span className="text-xs font-semibold text-gray-900">{fmt(summary?.contractValue)}</span>
            </div>
            {/* Avenants signes — la meme liste que celle qui fait le contrat revise */}
            <div>
              <div className="px-5 pt-3 pb-1.5">
                <span className="text-xs text-gray-600">{t('subInvoices.summaryTab.signedChangeOrders')}</span>
              </div>
              {summary?.signedChangeOrders?.length > 0 ? (
                <div className="px-5 pb-3 space-y-2.5">
                  {summary?.signedChangeOrders?.map((co, idx) => {
                    // Repli sans co_number : « Avenant n », n = numero AV sinon rang dans la liste.
                    const n = co?.avNumber ?? idx + 1;
                    const amount = co?.amount || 0;
                    // Un avenant cree depuis un extra porte deja le titre de l'extra au debut
                    // de sa description (« titre — lignes »). On retire ce prefixe A L'AFFICHAGE
                    // seulement : rien n'est modifie en base.
                    const title = co?.sourceExtraTitle?.trim() || '';
                    const description = co?.description?.trim() || '';
                    let detail = description;
                    if (title && description?.startsWith(title)) {
                      const rest = description?.slice(title?.length);
                      const dash = rest?.match(/^\s*—\s*/);
                      if (dash) detail = rest?.slice(dash?.[0]?.length)?.trim();
                      else if (rest?.trim() === '') detail = '';
                    }
                    return (
                      <div key={co?.id} className="flex items-start justify-between gap-4 pl-3">
                        <div className="min-w-0">
                          <p className="text-xs text-gray-800">
                            <span className="font-semibold">
                              {co?.coNumber || t('subInvoices.summaryTab.changeOrderLabel', { n })}
                            </span>
                            {title && <span className="ml-1.5">{title}</span>}
                          </p>
                          {detail && (
                            <p className="mt-0.5 text-[11px] text-gray-500 whitespace-pre-line break-words">{detail}</p>
                          )}
                        </div>
                        <span className={`text-xs font-semibold whitespace-nowrap ${amount < 0 ? 'text-red-600' : 'text-green-700'}`}>
                          {amount < 0 ? `−${fmt(-amount)}` : `+${fmt(amount)}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="px-5 pb-3 pl-8 text-xs text-gray-400">{t('subInvoices.summaryTab.noSignedChangeOrder')}</p>
              )}
            </div>
            {/* Revised Contract */}
            <div className="flex items-center justify-between px-5 py-3.5 bg-gray-50">
              <span className="font-semibold text-gray-800">{t('subInvoices.summaryTab.revisedContract')}</span>
              <span className="text-sm font-bold text-gray-900">{fmt(summary?.revisedContract)}</span>
            </div>
            {/* Total Invoiced */}
            <div>
              <div className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-600">{t('subInvoices.summaryTab.totalInvoiced')}</span>
                  {summary?.invoicesBreakdown?.length > 0 && (
                    <button
                      onClick={() => setInvoicesOpen(o => !o)}
                      className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-gray-600 transition-colors ml-1"
                    >
                      <Icon name={invoicesOpen ? 'ChevronUp' : 'ChevronDown'} size={12} />
                      <span>{invoicesOpen ? t('subInvoices.summaryTab.hideBreakdown') : t('subInvoices.summaryTab.showBreakdown')}</span>
                    </button>
                  )}
                </div>
                <span className="text-xs font-semibold text-gray-900">{fmt(summary?.totalInvoiced)}</span>
              </div>
              {/* Invoices Breakdown — collapsible */}
              {invoicesOpen && summary?.invoicesBreakdown?.length > 0 && (
                <div className="px-5 pb-3 bg-gray-50/60">
                  <div className="space-y-3 pt-1">
                    {summary?.invoicesBreakdown?.map((inv) => (
                      <div key={inv?.invoiceId}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-semibold text-gray-600">
                            {inv?.invoiceNumber ? `Invoice #${inv?.invoiceNumber}` : 'Invoice'}
                            {inv?.invoiceDate ? ` — ${fmtDate(inv?.invoiceDate, language)}` : ''}
                          </span>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={inv?.status} />
                            <span className="text-[11px] font-semibold text-gray-700 whitespace-nowrap">{fmt(inv?.total)}</span>
                          </div>
                        </div>
                        <div className="space-y-1 pl-3">
                          {inv?.lines?.map((line, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <span className="text-xs text-gray-400 italic truncate pr-4">
                                {line?.description || '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* Remaining / Overbilled */}
            <div className={`flex items-center justify-between px-5 py-3.5 ${isOverbilled ? 'bg-red-50' : 'bg-emerald-50'}`}>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold ${isOverbilled ? 'text-red-700' : 'text-emerald-700'}`}>
                  {isOverbilled ? t('subInvoices.summaryTab.overbilled') : t('subInvoices.summaryTab.remainingToBill')}
                </span>
                {isOverbilled && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                    <Icon name="AlertTriangle" size={10} />
                    {t('subInvoices.overBudget')}
                  </span>
                )}
              </div>
              <span className={`text-sm font-bold ${isOverbilled ? 'text-red-700' : 'text-emerald-700'}`}>
                {isOverbilled ? `−${fmt(Math.abs(summary?.remaining))}` : fmt(summary?.remaining)}
              </span>
            </div>
            {/* Billing Progress Bar */}
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-gray-500">{t('subInvoices.summaryTab.billingProgress')}</span>
                <span className="text-[11px] font-semibold text-gray-700">{progressPct?.toFixed(1)}%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────
const SubInvoicesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useTranslation();

  const initialProject = location?.state?.project;

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(initialProject || null);
  const [invoices, setInvoices] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [subcontracts, setSubcontracts] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [extras, setExtras] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('subInvoices_statusFilter') || 'invoices';
  });
  const [selectedSummaryContractId, setSelectedSummaryContractId] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [approvingInvoice, setApprovingInvoice] = useState(null);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoiceSubcontractId, setSelectedInvoiceSubcontractId] = useState(() => {
    return localStorage.getItem('subInvoices_subcontractFilter') || '';
  });

  // ─── Search query state ───────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  // Filtres additionnels onglet Factures (en memoire, non persistes).
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');

  // ─── Sort state for Invoices tab (lifted so exports can use it) ──────────
  const [invoicesSortConfig, setInvoicesSortConfig] = useState({ key: null, direction: 'asc' });

  // ─── Bulk selection state ─────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [actionFeedback, setActionFeedback] = useState(null);
  const feedbackTimerRef = useRef(null);
  // Remise à reçue / soumis d'une facture déjà approuvée ou payée : { newStatus, ids, impacts }
  const [resetPrompt, setResetPrompt] = useState(null);
  const [resetting, setResetting] = useState(false);

  const showFeedback = (type) => {
    clearTimeout(feedbackTimerRef?.current);
    setActionFeedback(type);
    feedbackTimerRef.current = setTimeout(() => setActionFeedback(null), 2500);
  };

  const handleToggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback((ids) => {
    setSelectedIds(prev => {
      const allSelected = ids?.every(id => prev?.has(id));
      if (allSelected) {
        const next = new Set(prev);
        ids?.forEach(id => next?.delete(id));
        return next;
      } else {
        const next = new Set(prev);
        ids?.forEach(id => next?.add(id));
        return next;
      }
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Total monetaire de la selection, affiche dans la barre d'actions groupees
  const selectedTotal = useMemo(
    () => (invoices || [])
      ?.filter((inv) => selectedIds?.has(inv?.id))
      ?.reduce((sum, inv) => sum + (parseFloat(inv?.invoiceTotalWithTax) || 0), 0),
    [invoices, selectedIds]
  );

  const handleBulkSubmit = useCallback(async () => {
    if (selectedIds?.size === 0) return;
    const ids = Array.from(selectedIds);
    const idsSnapshot = new Set(selectedIds);

    // 1. Optimistic UI update — immediately reflect submitted state
    setInvoices(prev =>
      prev?.map(inv =>
        idsSnapshot?.has(inv?.id)
          ? { ...inv, status: 'pending_approval' }
          : inv
      )
    );

    // 2. Close bulk bar instantly + show toast
    setSelectedIds(new Set());
    showFeedback('submitted');

    // 3. Run DB update in background
    try {
      await Promise.all(ids?.map(id => subInvoiceService?.submitForApproval(id)));
    } catch (err) {
      console.error('[SubInvoicesPage] bulkSubmit error:', err);
      await fetchData();
    }
  }, [selectedIds, invoices]);

  // Écrit le statut. resetIds : factures dont paiements + approbations sont
  // d'abord radiés par la RPC (qui les met à received).
  const applyStatusChange = async (ids, newStatus, resetIds = []) => {
    const idsSnapshot = new Set(ids);
    const resetSet = new Set(resetIds);

    // 1. Optimistic UI update — immediately reflect new status
    setInvoices(prev =>
      prev?.map(inv =>
        idsSnapshot?.has(inv?.id)
          ? { ...inv, status: newStatus }
          : inv
      )
    );

    // 2. Close bulk bar instantly + show toast
    setSelectedIds(new Set());
    showFeedback(newStatus);

    // 3. Run DB updates
    try {
      for (const resetId of resetIds) {
        await subInvoiceService?.resetToReceived(resetId);
      }
      // La RPC a déjà mis received ; pour « Soumis », l'update direct suit.
      const directIds = ids?.filter(id => !resetSet?.has(id) || newStatus !== 'received');
      const results = await Promise.all(
        directIds?.map(id =>
          supabase?.from('sub_invoices')?.update({ status: newStatus, updated_at: new Date()?.toISOString() })?.eq('id', id)
        )
      );
      const failed = results?.find(r => r?.error);
      if (failed) throw failed.error;
      // Rechargement silencieux : paiements / approbations radiés, montants à jour.
      const fresh = await subInvoiceService?.getByProjectId(id);
      if (fresh?.length) setInvoices(fresh);
    } catch (err) {
      console.error('[SubInvoicesPage] bulkStatusChange error:', err);
      showFeedback(resetIds?.length > 0 ? 'resetError' : 'statusError');
      await fetchData();
    }
  };

  const handleBulkStatusChange = useCallback(async (newStatus) => {
    if (selectedIds?.size === 0) return;
    const ids = Array.from(selectedIds);

    // Garde : une facture avec paiement ou approbation ne redescend pas
    // sans confirmation. Lu en DB, pas dans l'état écran.
    if (newStatus === 'received' || newStatus === 'pending_approval') {
      let impacts;
      try {
        impacts = (await Promise.all(ids?.map(invId => subInvoiceService?.getResetImpact(invId))))
          ?.filter(x => x?.payments?.length > 0 || x?.approvals?.length > 0);
      } catch (err) {
        console.error('[SubInvoicesPage] getResetImpact error:', err);
        showFeedback('statusError');
        return;
      }
      if (impacts?.length > 0) {
        setResetPrompt({ newStatus, ids, impacts });
        return;
      }
    }

    await applyStatusChange(ids, newStatus);
  }, [selectedIds, invoices]);

  const handleConfirmReset = async () => {
    if (!resetPrompt) return;
    setResetting(true);
    try {
      await applyStatusChange(resetPrompt?.ids, resetPrompt?.newStatus, resetPrompt?.impacts?.map(x => x?.invoiceId));
    } finally {
      setResetting(false);
      setResetPrompt(null);
    }
  };

  const handleExportSelected = useCallback(() => {
    const selectedInvoices = invoices?.filter(inv => selectedIds?.has(inv?.id));
    if (selectedInvoices?.length === 0) return;

    const STATUS_LABELS = {
      received: 'Received',
      pending_approval: 'Submitted',
      approved_to_pay: 'Approved to Pay',
      paid: 'Paid',
      rejected: 'Rejected'
    };

    const headers = EXPORT_COLUMNS(t);
    let rows = selectedInvoices?.map(inv => [
      inv?.invoiceNumber || '',
      inv?.invoiceDate || '',
      inv?.subcontractor?.companyName || '',
      inv?.invoiceType || '',
      (parseFloat(inv?.invoiceTotalWithTax) || 0)?.toFixed(2),
      (parseFloat(inv?.balance) || 0)?.toFixed(2),
      STATUS_LABELS?.[inv?.status] || inv?.status || ''
    ]);

    const csvContent = [headers, ...rows]
      ?.map(row => row?.map(cell => `"${String(cell)?.replace(/"/g, '""')}"`)?.join(','))
      ?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL?.createObjectURL(blob);
    const link = document?.createElement('a');
    link.href = url;
    link.download = `invoices_export_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    document?.body?.appendChild(link);
    link?.click();
    document?.body?.removeChild(link);
    URL?.revokeObjectURL(url);
  }, [selectedIds, invoices]);

  // ─── Bulk export: selected rows only ─────────────────────────────────────
  const handleBulkExportCSV = useCallback(() => {
    const STATUS_LABELS = {
      received: 'Received',
      pending_approval: 'Submitted',
      approved_to_pay: 'Approved to Pay',
      paid: 'Paid',
      rejected: 'Rejected'
    };
    const selectedInvoices = invoices?.filter(inv => selectedIds?.has(inv?.id));
    if (selectedInvoices?.length === 0) return;
    const headers = EXPORT_COLUMNS(t);
    const csvRows = selectedInvoices?.map(inv => [
      inv?.invoiceNumber || '',
      inv?.invoiceDate || '',
      inv?.subcontractor?.companyName || '',
      inv?.invoiceType || '',
      (parseFloat(inv?.invoiceTotalWithTax) || 0)?.toFixed(2),
      (parseFloat(inv?.balance) || 0)?.toFixed(2),
      STATUS_LABELS?.[inv?.status] || inv?.status || ''
    ]);
    const csvContent = [headers, ...csvRows]
      ?.map(row => row?.map(cell => `"${String(cell)?.replace(/"/g, '""')}"`)?.join(','))
      ?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL?.createObjectURL(blob);
    const link = document?.createElement('a');
    link.href = url;
    link.download = `sub-invoices_selected_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    document?.body?.appendChild(link);
    link?.click();
    document?.body?.removeChild(link);
    URL?.revokeObjectURL(url);
  }, [selectedIds, invoices]);

  const handleBulkExportPDF = useCallback(() => {
    const STATUS_CONFIG = {
      received:        { label: 'Received',        bg: '#f3f4f6', color: '#4b5563' },
      pending_approval:{ label: 'Submitted',        bg: '#dbeafe', color: '#1d4ed8' },
      approved_to_pay: { label: 'Approved to Pay',  bg: '#dcfce7', color: '#16a34a' },
      paid:            { label: 'Paid',             bg: '#d1fae5', color: '#065f46' },
      rejected:        { label: 'Rejected',         bg: '#fee2e2', color: '#b91c1c' }
    };
    const selectedInvoices = invoices?.filter(inv => selectedIds?.has(inv?.id));
    if (selectedInvoices?.length === 0) return;
    const tableRows = selectedInvoices?.map(inv => {
      const cfg = STATUS_CONFIG?.[inv?.status] || STATUS_CONFIG?.received;
      const badge = `<span style="display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:${cfg?.bg};color:${cfg?.color}">${cfg?.label}</span>`;
      return `
      <tr>
        <td>${inv?.invoiceNumber || '—'}</td>
        <td>${inv?.invoiceDate || '—'}</td>
        <td>${inv?.subcontractor?.companyName || '—'}</td>
        <td>${inv?.invoiceType || '—'}</td>
        <td style="text-align:right">${fmtCAD(inv?.invoiceTotalWithTax)}</td>
        <td>${badge}</td>
      </tr>
    `;
    })?.join('');
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Sub Invoices — Selected</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; margin: 32px; }
    h1 { font-size: 16px; margin-bottom: 4px; }
    .sub { color: #666; font-size: 11px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f3f4f6; text-align: left; padding: 6px 10px; font-size: 10px; color: #555; border-bottom: 1px solid #e5e7eb; }
    td { padding: 6px 10px; border-bottom: 1px solid #f3f4f6; }
  </style>
</head>
<body>
  <h1>Sub Invoices — Selected (${selectedInvoices?.length})</h1>
  <p class="sub">Exported ${formatDateLong(new Date(), language)}</p>
  <table>
    <thead>
      <tr>
        <th>Invoice #</th><th>Date</th><th>Subcontractor</th><th>Type</th><th style="text-align:right">Amount</th><th>Status</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
</body>
</html>`;
    const win = window?.open('', '_blank');
    if (win) {
      win?.document?.write(html);
      win?.document?.close();
      setTimeout(() => { win?.print(); }, 400);
    }
  }, [selectedIds, invoices]);

  // ─── Helper: get the rows currently visible based on active tab + filter + sort ──
  const getVisibleRows = useCallback(() => {
    // Step 1: filter by tab
    let rows;
    if (activeTab === 'invoices') {
      rows = selectedInvoiceSubcontractId
        ? invoices?.filter(inv => inv?.subcontractFilterId === selectedInvoiceSubcontractId)
        : invoices;
      // Step 1b: apply status + search filters (mirrors filteredInvoices)
      if (selectedStatusFilter !== 'all') {
        rows = rows?.filter(inv => inv?.status === selectedStatusFilter);
      }
      if (searchQuery?.trim()) {
        const q = searchQuery?.trim()?.toLowerCase();
        rows = rows?.filter(inv =>
          (inv?.subcontractor?.companyName || '')?.toLowerCase()?.includes(q) ||
          (inv?.invoiceNumber || '')?.toLowerCase()?.includes(q)
        );
      }
      // Step 2: apply sort (mirrors InvoicesTab sortedInvoices logic)
      const STATUS_ORDER = { received: 0, pending_approval: 1, approved_to_pay: 2, paid: 3 };
      if (!invoicesSortConfig?.key) {
        rows = [...(rows || [])]?.sort((a, b) => {
          const aOrder = STATUS_ORDER?.[a?.status] ?? 99;
          const bOrder = STATUS_ORDER?.[b?.status] ?? 99;
          return aOrder - bOrder;
        });
      } else {
        rows = [...(rows || [])]?.sort((a, b) => {
          let aVal, bVal;
          switch (invoicesSortConfig?.key) {
            case 'invoiceDate': aVal = a?.invoiceDate || ''; bVal = b?.invoiceDate || ''; break;
            case 'invoiceNumber': aVal = a?.invoiceNumber || ''; bVal = b?.invoiceNumber || ''; break;
            case 'subcontractor': aVal = a?.subcontractor?.companyName || ''; bVal = b?.subcontractor?.companyName || ''; break;
            case 'invoiceType': aVal = a?.invoiceType || ''; bVal = b?.invoiceType || ''; break;
            case 'invoiceTotal': aVal = parseFloat(a?.invoiceTotalWithTax) || 0; bVal = parseFloat(b?.invoiceTotalWithTax) || 0; break;
            case 'balance': aVal = parseFloat(a?.balance) || 0; bVal = parseFloat(b?.balance) || 0; break;
            case 'status': aVal = a?.status || ''; bVal = b?.status || ''; break;
            default: return 0;
          }
          if (aVal < bVal) return invoicesSortConfig?.direction === 'asc' ? -1 : 1;
          if (aVal > bVal) return invoicesSortConfig?.direction === 'asc' ? 1 : -1;
          return 0;
        });
      }
    } else if (activeTab === 'approval') {
      rows = invoices?.filter(inv => inv?.status === 'pending_approval');
    } else if (activeTab === 'payments') {
      rows = invoices?.filter(inv => inv?.status === 'approved_to_pay' || inv?.status === 'partially_paid' || inv?.status === 'paid');
    } else {
      rows = invoices;
    }
    return rows || [];
  }, [activeTab, invoices, selectedInvoiceSubcontractId, selectedStatusFilter, invoicesSortConfig, searchQuery]);

  const handleExportCSV = useCallback(() => {
    const STATUS_LABELS = {
      received: 'Received',
      pending_approval: 'Submitted',
      approved_to_pay: 'Approved to Pay',
      paid: 'Paid',
      rejected: 'Rejected'
    };
    let rows = getVisibleRows();
    const headers = EXPORT_COLUMNS(t);
    const csvRows = rows?.map(inv => [
      inv?.invoiceNumber || '',
      inv?.invoiceDate || '',
      inv?.subcontractor?.companyName || '',
      inv?.invoiceType || '',
      (parseFloat(inv?.invoiceTotalWithTax) || 0)?.toFixed(2),
      (parseFloat(inv?.balance) || 0)?.toFixed(2),
      STATUS_LABELS?.[inv?.status] || inv?.status || ''
    ]);
    const csvContent = [headers, ...csvRows]
      ?.map(row => row?.map(cell => `"${String(cell)?.replace(/"/g, '""')}"`)?.join(','))
      ?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL?.createObjectURL(blob);
    const link = document?.createElement('a');
    link.href = url;
    link.download = `sub-invoices_${activeTab}_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    document?.body?.appendChild(link);
    link?.click();
    document?.body?.removeChild(link);
    URL?.revokeObjectURL(url);
  }, [getVisibleRows, activeTab]);

  const handleExportPDF = useCallback(() => {
    const STATUS_CONFIG = {
      received:        { label: 'Received',        bg: '#f3f4f6', color: '#4b5563' },
      pending_approval:{ label: 'Submitted',        bg: '#dbeafe', color: '#1d4ed8' },
      approved_to_pay: { label: 'Approved to Pay',  bg: '#dcfce7', color: '#16a34a' },
      paid:            { label: 'Paid',             bg: '#d1fae5', color: '#065f46' },
      rejected:        { label: 'Rejected',         bg: '#fee2e2', color: '#b91c1c' }
    };
    let rows = getVisibleRows();
    const tabLabels = { invoices: 'Invoices', approval: 'Invoice Approval', payments: 'Payments', summary: 'Summary' };
    const tableRows = rows?.map(inv => {
      const cfg = STATUS_CONFIG?.[inv?.status] || STATUS_CONFIG?.received;
      const badge = `<span style="display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:${cfg?.bg};color:${cfg?.color}">${cfg?.label}</span>`;
      return `
      <tr>
        <td>${inv?.invoiceNumber || '—'}</td>
        <td>${inv?.invoiceDate || '—'}</td>
        <td>${inv?.subcontractor?.companyName || '—'}</td>
        <td>${inv?.invoiceType || '—'}</td>
        <td style="text-align:right">${fmtCAD(inv?.invoiceTotalWithTax)}</td>
        <td>${badge}</td>
      </tr>
    `;
    })?.join('');
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Sub Invoices — ${tabLabels?.[activeTab] || activeTab}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; margin: 32px; }
    h1 { font-size: 16px; margin-bottom: 4px; }
    .sub { color: #666; font-size: 11px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f3f4f6; text-align: left; padding: 6px 10px; font-size: 10px; color: #555; border-bottom: 1px solid #e5e7eb; }
    td { padding: 6px 10px; border-bottom: 1px solid #f3f4f6; }
  </style>
</head>
<body>
  <h1>Sub Invoices — ${tabLabels?.[activeTab] || activeTab}</h1>
  <p class="sub">Exported ${formatDateLong(new Date(), language)}</p>
  <table>
    <thead>
      <tr>
        <th>Invoice #</th><th>Date</th><th>Subcontractor</th><th>Type</th><th style="text-align:right">Amount</th><th>Status</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
</body>
</html>`;
    const win = window?.open('', '_blank');
    if (win) {
      win?.document?.write(html);
      win?.document?.close();
      setTimeout(() => { win?.print(); }, 400);
    }
  }, [getVisibleRows, activeTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    localStorage.setItem('subInvoices_statusFilter', tabId);
    setSelectedIds(new Set());
  };

  const handleSubcontractFilterChange = (val) => {
    const value = val || '';
    setSelectedInvoiceSubcontractId(value);
    if (value) {
      localStorage.setItem('subInvoices_subcontractFilter', value);
    } else {
      localStorage.removeItem('subInvoices_subcontractFilter');
    }
  };

  const handleClearFilters = () => {
    setActiveTab('invoices');
    setSelectedInvoiceSubcontractId('');
    setSearchQuery('');
    setSelectedStatusFilter('all');
    localStorage.removeItem('subInvoices_statusFilter');
    localStorage.removeItem('subInvoices_subcontractFilter');
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invData, subData, costData, extData, subcontractData, prData] = await Promise.all([
        subInvoiceService?.getByProjectId(id),
        subcontractorService?.getAll ? subcontractorService?.getAll() : (subcontractorService?.getByProjectId?.(id) || Promise.resolve([])),
        projectCostService?.getByProjectId ? projectCostService?.getByProjectId(id) : Promise.resolve([]),
        projectExtrasService?.getByProjectId(id),
        subcontractService?.getByProjectId(id),
        paymentRequestService?.getByProjectId(id)
      ]);
      setInvoices(invData || []);
      setSubcontractors(subData || []);
      setCostCodes(costData || []);
      setExtras(extData || []);
      setSubcontracts(subcontractData || []);
      setPaymentRequests(prData || []);
    } catch (err) {
      console.error('[SubInvoicesPage] fetchData error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchData();
      if (!initialProject) {
        projectService?.getById(id)?.then(data => setProject(data))?.catch(err => console.error(err));
      }
    }
  }, [id]);

  // KPI calculations
  const kpis = {
    totalInvoices: invoices?.length,
    totalInvoiced: invoices?.reduce((s, inv) => s + (inv?.invoiceTotal || 0), 0),
    approvedToPay: invoices?.filter(inv => inv?.status === 'approved_to_pay')?.reduce((s, inv) => s + (inv?.netPayable || 0), 0),
    totalPaid: invoices?.filter(inv => inv?.status === 'paid')?.reduce((s, inv) => s + (inv?.totalPaid || 0), 0)
  };

  const tabs = [
    { id: 'invoices',  label: t('subInvoices.tabs.invoices') },
    { id: 'approval',  label: t('subInvoices.tabs.approval') },
    { id: 'payments',  label: t('subInvoices.tabs.payments') },
    { id: 'summary',   label: t('subInvoices.tabs.summary') }
  ];

  const handleDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await subInvoiceService?.delete(pendingDeleteId);
      setInvoices(prev => prev?.filter(inv => inv?.id !== pendingDeleteId));
    } catch (err) {
      console.error('[SubInvoicesPage] delete error:', err);
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleInvoiceCreated = (inv) => {
    setInvoices(prev => [inv, ...prev]);
  };

  const handleInvoiceUpdated = (updated) => {
    setInvoices(prev => prev?.map(inv => inv?.id === updated?.id ? updated : inv));
  };

  const handleSubmitForApproval = async (inv) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const updated = await subInvoiceService?.submitForApproval(inv?.id);
      setInvoices(prev => prev?.map(i => i?.id === updated?.id ? updated : i));
    } catch (err) {
      console.error('[SubInvoicesPage] submitForApproval error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproved = (updated) => {
    setInvoices(prev => prev?.map(inv => inv?.id === updated?.id ? updated : inv));
    setApprovingInvoice(null);
    setActiveTab('payments');
  };

  const handleRequestRetentionRelease = async (inv) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const updated = await subInvoiceService?.requestRetentionRelease(inv?.id);
      setInvoices(prev => prev?.map(i => i?.id === updated?.id ? updated : i));
      setActiveTab('approval');
    } catch (err) {
      console.error('[SubInvoicesPage] requestRetentionRelease error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaid = (updated) => {
    setInvoices(prev => prev?.map(inv => inv?.id === updated?.id ? updated : inv));
    setPayingInvoice(null);
  };

  // Filtered invoices for the Invoices tab
  const filteredInvoices = useMemo(() => {
    let rows = selectedInvoiceSubcontractId
      ? invoices?.filter(inv =>
        inv?.subcontractFilterId === selectedInvoiceSubcontractId
      )
      : invoices;
    if (selectedStatusFilter !== 'all') {
      rows = rows?.filter(inv => inv?.status === selectedStatusFilter);
    }
    if (searchQuery?.trim()) {
      const q = searchQuery?.trim()?.toLowerCase();
      rows = rows?.filter(inv =>
        (inv?.subcontractor?.companyName || '')?.toLowerCase()?.includes(q) ||
        (inv?.invoiceNumber || '')?.toLowerCase()?.includes(q)
      );
    }
    return rows;
  }, [invoices, selectedInvoiceSubcontractId, selectedStatusFilter, searchQuery]);

  const noFilterResults = (selectedInvoiceSubcontractId || searchQuery?.trim()) && filteredInvoices?.length === 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Icon name="Loader2" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-[#FAFBFC] pt-0 pb-0 px-0 h-full flex flex-col">
      {/* Header Block with KPI Cards */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="FileText" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-[#2C3E50]">
                {t('subInvoices.title')}
              </h1>
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.projectNumber || t('common.na')}
              </span>
              <span className="text-sm text-primary font-normal">-</span>
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.name || t('common.loading')}
              </span>
            </div>
            <p className="text-xs text-gray-500">{t('subInvoices.subtitle')}</p>
          </div>
          <div className="flex-1 flex items-center justify-end gap-2">
            <StatsCard
              title={t('subInvoices.kpi.totalInvoices')}
              value={kpis?.totalInvoices}
              sublabel=""
              iconName="FileText"
              iconColor="text-blue-500"
              valueColor="text-blue-600"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('subInvoices.kpi.totalInvoiced')}
              value={fmt(kpis?.totalInvoiced)}
              sublabel=""
              iconName="DollarSign"
              iconColor="text-gray-400"
              valueColor="text-gray-900"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('subInvoices.kpi.approvedToPay')}
              value={fmt(kpis?.approvedToPay)}
              sublabel=""
              iconName="CheckCircle"
              iconColor="text-green-500"
              valueColor="text-green-600"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('subInvoices.kpi.paid')}
              value={fmt(kpis?.totalPaid)}
              sublabel=""
              iconName="Banknote"
              iconColor="text-emerald-500"
              valueColor="text-emerald-700"
              titleWeight="font-medium"
            />
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="mb-4">
        <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1 shadow-sm w-fit">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => handleTabChange(tab?.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                activeTab === tab?.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {tab?.label}
            </button>
          ))}
        </div>
        {activeTab === 'invoices' && (
          <div className="flex items-center justify-between mt-3">
            {/* Left: search + subcontract + status filters */}
            <div className="flex items-center gap-2">
              {/* Search box */}
              <div className="relative">
                <Icon name="Search" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  placeholder={t('subInvoices.filters.searchPlaceholder')}
                  className="pl-7 pr-8 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-72 transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Icon name="X" size={11} />
                  </button>
                )}
              </div>
              <SubcontractCombobox
                subcontracts={subcontracts || []}
                value={selectedInvoiceSubcontractId || null}
                onChange={handleSubcontractFilterChange}
                placeholder={t('subInvoices.filters.chooseSubcontract')}
                size="sm"
                className="w-64"
              />
              <StatusFilterDropdown value={selectedStatusFilter} onChange={setSelectedStatusFilter} t={t} />
              {(selectedInvoiceSubcontractId || searchQuery?.trim() || selectedStatusFilter !== 'all') && (
                <button
                  onClick={handleClearFilters}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                  title="Clear filters"
                >
                  <Icon name="X" size={12} />
                  {t('subInvoices.clearFilters')}
                </button>
              )}
            </div>
            {/* Right: CSV / PDF / Add Invoice */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportCSV}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                title="Export as CSV"
              >
                <Icon name="FileSpreadsheet" size={13} className="text-green-500" />
                CSV
              </button>
              <button
                onClick={handleExportPDF}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                title="Export as PDF"
              >
                <Icon name="FileText" size={13} className="text-red-500" />
                PDF
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm whitespace-nowrap"
              >
                <Icon name="Plus" size={13} />
                {t('invoice.form.title')}
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Tab Content */}
      <div className="space-y-4 min-h-0 flex flex-col">
        {activeTab === 'invoices' && (
          <>
            {noFilterResults ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center mb-3">
                  <Icon name="SearchX" size={22} className="text-primary" />
                </div>
                <p className="text-sm font-medium text-gray-700">No invoices found for this subcontractor</p>
                <p className="text-xs text-gray-400 mt-1">Try selecting a different subcontractor or clear the filter</p>
              </div>
            ) : (
              <InvoicesTab
                invoices={filteredInvoices}
                onSubmitForApproval={handleSubmitForApproval}
                onRequestRetentionRelease={handleRequestRetentionRelease}
                onDelete={setPendingDeleteId}
                onEdit={setEditingInvoice}
                projectId={id}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                sortConfig={invoicesSortConfig}
                onSortChange={setInvoicesSortConfig}
              />
            )}
          </>
        )}
        {activeTab === 'approval' && (
          <InvoiceApprovalTab
            invoices={invoices}
            subcontracts={subcontracts}
            onApprove={setApprovingInvoice}
            projectId={id}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
          />
        )}
        {activeTab === 'payments' && (
          <PaymentsTab
            invoices={invoices}
            subcontracts={subcontracts}
            paymentRequests={paymentRequests}
            onRecordPayment={setPayingInvoice}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
          />
        )}
        {activeTab === 'summary' && (
          <SubSummaryTab
            subcontracts={subcontracts}
            projectId={id}
            selectedId={selectedSummaryContractId}
            onSelectedChange={setSelectedSummaryContractId}
            isActive={activeTab === 'summary'}
          />
        )}
      </div>
      {/* Modals */}
      {pendingDeleteId && (
        <ConfirmDialog
          isOpen={!!pendingDeleteId}
          title="Delete Invoice"
          message="Are you sure? This cannot be undone."
          confirmVariant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={handleDelete}
          onCancel={() => setPendingDeleteId(null)}
        />
      )}
      {showAddModal && (
        <AddInvoiceModal
          projectId={id}
          subcontractors={subcontractors}
          subcontracts={subcontracts}
          costCodes={costCodes}
          extras={extras}
          paymentRequests={paymentRequests}
          onClose={() => setShowAddModal(false)}
          onSave={handleInvoiceCreated}
          initialData={selectedInvoiceSubcontractId ? { subcontractId: selectedInvoiceSubcontractId } : null}
          mode="add"
        />
      )}
      {editingInvoice && (
        <AddInvoiceModal
          projectId={id}
          subcontractors={subcontractors}
          subcontracts={subcontracts}
          costCodes={costCodes}
          extras={extras}
          paymentRequests={paymentRequests}
          mode="edit"
          initialData={editingInvoice}
          onClose={() => setEditingInvoice(null)}
          onSave={handleInvoiceUpdated}
        />
      )}
      {approvingInvoice && (
        <ApproveModal
          invoice={approvingInvoice}
          onClose={() => setApprovingInvoice(null)}
          onApproved={handleApproved}
        />
      )}
      {payingInvoice && (
        <RecordPaymentModal
          invoice={payingInvoice}
          onClose={() => setPayingInvoice(null)}
          onPaid={handlePaid}
        />
      )}
      {resetPrompt && (
        <ResetToReceivedDialog
          prompt={resetPrompt}
          invoices={invoices}
          loading={resetting}
          onConfirm={handleConfirmReset}
          onCancel={() => setResetPrompt(null)}
        />
      )}
      {/* Bulk Action Bar */}
      <BulkActionBar
        count={selectedIds?.size}
        total={fmtCAD(selectedTotal)}
        actions={[
          {
            key: 'submit',
            label: t('bulk.submit'),
            icon: 'Send',
            variant: 'info',
            onClick: handleBulkSubmit,
          },
          {
            key: 'status',
            label: t('bulk.changeStatus'),
            icon: 'RefreshCw',
            variant: 'default',
            dropdown: [
              {
                label: t('subInvoices.status.received'),
                icon: 'Inbox',
                onClick: () => handleBulkStatusChange('received'),
              },
              {
                label: t('subInvoices.status.pending_approval'),
                icon: 'Send',
                onClick: () => handleBulkStatusChange('pending_approval'),
              },
            ],
          },
          {
            key: 'export',
            label: t('bulk.export'),
            icon: 'Download',
            variant: 'default',
            dropdown: [
              { label: t('bulk.exportCsv'), icon: 'FileSpreadsheet', onClick: () => handleBulkExportCSV?.() },
              { label: t('bulk.exportPdf'), icon: 'FileText',        onClick: () => handleBulkExportPDF?.() },
            ],
          },
        ]}
        onClear={handleClearSelection}
        clearLabel={t('bulk.clear')}
      />
      {/* Action Feedback Toast */}
      {actionFeedback && (
        <div
          className={`fixed bottom-6 left-1/2 z-50 text-white px-4 py-2 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium ${
            actionFeedback === 'approved' || actionFeedback === 'approved_to_pay' ? 'bg-green-600' :
            actionFeedback === 'submitted' || actionFeedback === 'pending_approval' ? 'bg-blue-600' :
            actionFeedback === 'rejected' || actionFeedback === 'resetError' || actionFeedback === 'statusError' ? 'bg-red-500' : 'bg-gray-600'
          }`}
          style={{
            transform: 'translateX(-50%)',
            animation: 'feedbackSlideUp 0.25s ease-out forwards',
          }}
        >
          {(actionFeedback === 'approved' || actionFeedback === 'approved_to_pay') && <Icon name="CheckCircle2" size={15} />}
          {(actionFeedback === 'submitted' || actionFeedback === 'pending_approval') && <Icon name="Send" size={15} />}
          {(actionFeedback === 'rejected' || actionFeedback === 'resetError' || actionFeedback === 'statusError') && <Icon name="XCircle" size={15} />}
          {actionFeedback !== 'approved' && actionFeedback !== 'approved_to_pay' && actionFeedback !== 'submitted' && actionFeedback !== 'pending_approval' && actionFeedback !== 'rejected' && actionFeedback !== 'resetError' && actionFeedback !== 'statusError' && <Icon name="CheckCircle2" size={15} />}
          {actionFeedback === 'approved' || actionFeedback === 'approved_to_pay' ? 'Approved' :
           actionFeedback === 'submitted' || actionFeedback === 'pending_approval' ? 'Submitted' :
           actionFeedback === 'rejected' ? 'Rejected' :
           actionFeedback === 'resetError' ? t('subInvoices.resetToReceived.error') :
           actionFeedback === 'statusError' ? t('subInvoices.resetToReceived.statusError') :
           actionFeedback === 'paid' ? t('subInvoices.toast.markedPaid') : t('subInvoices.toast.updated')}
          <style>{`
            @keyframes feedbackSlideUp {
              from { opacity: 0; transform: translateX(-50%) translateY(10px); }
              to   { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default SubInvoicesPage;